// vendor.js
// ---------------------------
// This file is intended for importing third-party libraries and dependencies
// that provide utility functions or enhance the general functionality of the application.
//
// Guidelines:
// - Import libraries here that are shared across multiple parts of the application.
// - Keep application-specific JavaScript in separate files to maintain a clear separation.
// ---------------------------
// Import Fontsource here as a javascript instead of css/sass/scss.
// Otherwise, we will encounter missing modules during the style imports.
// Importing the Inter font for use across the project.
import '@fontsource/inter';

// Importing Popper.js for positioning tooltips, dropdowns, etc.
// Popper.js is a library used to position popovers, tooltips, and other elements
// that need to appear near a reference element.
import '@popperjs/core';

// Alpine.js CSP build for Content Security Policy compatibility
// This build doesn't require 'unsafe-eval' and works with strict CSP
import Alpine from '@alpinejs/csp';
import themeToggle, { withThemeAware } from './theme-toggle.js';
import { enqueueToast, initializeToasts } from './toasts.js';
import { collapsibleSidebar } from './components/sidebar.js';
import { bottomTabBar } from './components/bottom-tab-bar.js';

// Register Alpine.data() components for CSP compatibility
Alpine.data('mobileMenu', () => ({
  open: false,
  show() {
    this.open = true;
  },
  hide() {
    this.open = false;
  },
}));

Alpine.data('themeButton', () => withThemeAware({}));

Alpine.data('mlLayout', () =>
  withThemeAware({
    mobileSidebarOpen: false,
    openMobileSidebar() {
      this.mobileSidebarOpen = true;
    },
    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
    },
  }),
);

Alpine.data('modal', () => ({
  open: false,
  show() {
    this.open = true;
  },
  hide() {
    this.open = false;
  },
}));

Alpine.data('dismissible', () => ({
  visible: true,
  dismiss() {
    this.visible = false;
  },
}));

Alpine.data('cookieConsent', () => ({
  visible: false,
  init() {
    this.visible = !localStorage.getItem('cookie_consent');
  },
  accept() {
    localStorage.setItem('cookie_consent', 'accepted');
    this.visible = false;
  },
  reject() {
    localStorage.setItem('cookie_consent', 'rejected');
    this.visible = false;
  },
}));

Alpine.data('confirmDeleteModal', () => ({
  confirmText: '',
  open: false,
  selected: [],
  // Dynamic overrides — populated from `show-modal` event detail.
  // When empty, the modal falls back to its server-rendered defaults.
  actionUrl: '',
  dynamicTitle: '',
  dynamicBody: '',
  defaultTitle: '',
  defaultBody: '',
  defaultAction: '',

  init() {
    var self = this;
    var modalId = this.$el.id;

    // Cache server-rendered defaults so dynamic overrides can fall back to them.
    var titleEl = this.$el.querySelector('[data-modal-title]');
    if (titleEl) this.defaultTitle = (titleEl.textContent || '').trim();
    var bodyEl = this.$el.querySelector('[data-modal-body]');
    if (bodyEl) this.defaultBody = (bodyEl.textContent || '').trim();
    var formEl = this.$el.querySelector('[data-modal-form]');
    if (formEl) this.defaultAction = formEl.getAttribute('action') || '';

    // Wire trigger button: look for [data-modal-trigger] inside scope first,
    // then fall back to document-level [data-modal-trigger="<modalId>"]
    var trigger =
      this.$el.querySelector('[data-modal-trigger]') ||
      (modalId
        ? document.querySelector('[data-modal-trigger="' + modalId + '"]')
        : null);
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        self.confirmText = '';
        self.open = true;
      });
    }

    // Wire backdrop click to close
    var backdrop = this.$el.querySelector('[data-modal-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', function () {
        self.open = false;
      });
    }

    // Wire panel wrapper click (click outside panel) to close
    var panelWrap = this.$el.querySelector('[data-modal-panel-wrap]');
    if (panelWrap) {
      panelWrap.addEventListener('click', function (e) {
        if (e.target === panelWrap) self.open = false;
      });
    }

    // Wire cancel button
    var cancel = this.$el.querySelector('[data-modal-cancel]');
    if (cancel) {
      cancel.addEventListener('click', function () {
        self.open = false;
      });
    }

    // Wire typed confirmation input
    var confirmInput = this.$el.querySelector('[data-modal-confirm-input]');
    var submitBtn = this.$el.querySelector('[data-modal-submit]');
    var hiddenInput = this.$el.querySelector('[data-modal-confirm-hidden]');
    if (confirmInput && submitBtn) {
      var expectedValue = confirmInput.dataset.confirmValue || '';
      confirmInput.addEventListener('input', function (e) {
        self.confirmText = e.target.value;
        submitBtn.disabled = e.target.value !== expectedValue;
        if (hiddenInput) hiddenInput.value = e.target.value;
      });
    }

    // Listen for show-modal window events (from bulkSelect or any scope that
    // wants to open a shared modal with dynamic copy/action).
    this._showModalHandler = function (e) {
      var detail = e.detail || {};
      var targetId = typeof detail === 'string' ? detail : detail.id;
      if (targetId !== modalId) return;
      self.selected = detail.selected || [];
      self.actionUrl = detail.action || '';
      self.dynamicTitle = detail.title || '';
      self.dynamicBody = detail.body || '';
      self.confirmText = '';
      self.open = true;
    };
    window.addEventListener('show-modal', this._showModalHandler);

    // Wire escape key (store ref for cleanup)
    this._escHandler = function (e) {
      if (e.key === 'Escape' && self.open) {
        self.open = false;
      }
    };
    window.addEventListener('keydown', this._escHandler);
  },

  destroy() {
    if (this._escHandler) {
      window.removeEventListener('keydown', this._escHandler);
    }
    if (this._showModalHandler) {
      window.removeEventListener('show-modal', this._showModalHandler);
    }
  },

  show() {
    this.confirmText = '';
    this.open = true;
  },
  close() {
    this.open = false;
  },
}));

Alpine.data('bulkSelect', () => ({
  selecting: false,
  selected: [],
  allIds: [],

  init() {
    this.$nextTick(() => {
      // Auto-discover selectable IDs from data attributes
      const els = this.$el.querySelectorAll(
        '[data-action-item-id], [data-review-id]',
      );
      this.allIds = [...els].map(
        (el) => el.dataset.actionItemId || el.dataset.reviewId,
      );
    });
  },

  get count() {
    return this.selected.length;
  },
  get hasSelection() {
    return this.selected.length > 0;
  },
  get allSelected() {
    return (
      this.allIds.length > 0 && this.selected.length === this.allIds.length
    );
  },
  get indeterminate() {
    return (
      this.selected.length > 0 && this.selected.length < this.allIds.length
    );
  },

  enterSelectMode() {
    this.selecting = true;
  },
  exitSelectMode() {
    this.selecting = false;
    this.selected = [];
  },
  toggle(id) {
    const idx = this.selected.indexOf(id);
    idx === -1 ? this.selected.push(id) : this.selected.splice(idx, 1);
  },
  toggleAll() {
    this.selected = this.allSelected ? [] : [...this.allIds];
  },
  isSelected(id) {
    return this.selected.includes(id);
  },
  clear() {
    this.selected = [];
  },
  showModal(modalId) {
    window.dispatchEvent(
      new CustomEvent('show-modal', {
        detail: { id: modalId, selected: [...this.selected] },
      }),
    );
  },
}));

Alpine.data('supportingEvidenceDrawer', () => ({
  open: false,
  loading: false,
  isRefreshing: false,
  isAppending: false,
  title: 'Supporting Evidence',
  titleFull: 'Supporting Evidence',
  subtitle: '',
  listSummary: '',
  chips: [],
  activeSentiment: 'all',
  cardsHtml: '',
  cardCount: 0,
  totalReviews: 0,
  showingReviews: 0,
  hasMore: false,
  nextPage: null,
  pageSize: 0,
  viewAllUrl: '',
  fallbackUrl: '',
  drawerUrl: '',
  emptyMessage: 'No matching evidence in this range.',
  errorMessage: '',
  activeRequestId: 0,
  lastTriggerElement: null,
  activeTopicRow: null,
  lastAutoAppendAt: 0,
  loadMoreObserver: null,
  loadMoreObserverRoot: null,
  init() {
    this.$nextTick(() => {
      this._renderCardsHtml();
      this._syncLoadMoreObserver();
    });
  },
  async openFromEvent(event) {
    const detail = (event && event.detail) || {};
    const drawerUrl = String(detail.drawerUrl || '').trim();
    const fallbackUrl = String(detail.fallbackUrl || '').trim();
    const triggerElement =
      detail.triggerElement instanceof HTMLElement
        ? detail.triggerElement
        : null;

    this.fallbackUrl = fallbackUrl;
    this.drawerUrl = drawerUrl;
    this.lastTriggerElement = triggerElement
      ? triggerElement
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    this._setActiveTopicRow(this._resolveTopicRow(this.lastTriggerElement));

    if (!drawerUrl) {
      if (fallbackUrl) {
        window.location.assign(fallbackUrl);
      }
      return;
    }

    const isContextRefresh = this.open && this.cardCount > 0;
    const initialSentiment = this._defaultSentimentFromDrawerUrl(drawerUrl);

    this.open = true;
    this._setScrollLock(true);
    if (!isContextRefresh) {
      this._resetState();
    } else {
      this.errorMessage = '';
    }
    this.viewAllUrl = fallbackUrl || this.viewAllUrl;
    this.activeSentiment = initialSentiment;
    await this.fetchDrawerPayload({
      sentiment: initialSentiment,
      page: 1,
      append: false,
      preserveVisibleState: isContextRefresh,
    });
  },
  async setSentiment(sentiment) {
    const nextSentiment = this.normalizeSentiment(sentiment);
    if (
      !this.drawerUrl ||
      this.loading ||
      this.isRefreshing ||
      this.isAppending
    ) {
      return;
    }
    if (this.activeSentiment === nextSentiment && this.cardCount > 0) {
      return;
    }
    this.activeSentiment = nextSentiment;
    await this.fetchDrawerPayload({
      sentiment: nextSentiment,
      page: 1,
      append: false,
      preserveVisibleState: this.cardCount > 0,
    });
  },
  async loadMore() {
    if (
      !this.drawerUrl ||
      !this.hasMore ||
      this.loading ||
      this.isRefreshing ||
      this.isAppending
    ) {
      return;
    }
    const nextPage = Number(this.nextPage || 0);
    if (!nextPage || nextPage < 1) {
      return;
    }
    await this.fetchDrawerPayload({
      sentiment: this.activeSentiment,
      page: nextPage,
      append: true,
    });
  },
  onListScroll(event) {
    if (
      !this.hasMore ||
      this.loading ||
      this.isRefreshing ||
      this.isAppending
    ) {
      return;
    }
    const target = event && event.target;
    if (!target) {
      return;
    }
    const remaining =
      target.scrollHeight - (target.scrollTop + target.clientHeight);
    if (remaining > 120) {
      return;
    }
    this._triggerAutoLoad();
  },
  normalizeSentiment(sentiment) {
    const normalized = String(sentiment || '')
      .trim()
      .toLowerCase();
    if (
      normalized === 'negative' ||
      normalized === 'positive' ||
      normalized === 'neutral'
    ) {
      return normalized;
    }
    return 'all';
  },
  _defaultSentimentFromDrawerUrl(drawerUrl) {
    const value = String(drawerUrl || '').trim();
    if (!value) {
      return 'all';
    }
    try {
      const requestUrl = new URL(value, window.location.origin);
      return this.normalizeSentiment(
        requestUrl.searchParams.get('evidence_sentiment'),
      );
    } catch {
      return 'all';
    }
  },
  buildDrawerUrl({ sentiment, page }) {
    const requestUrl = new URL(this.drawerUrl, window.location.origin);
    const normalizedSentiment = this.normalizeSentiment(sentiment);
    requestUrl.searchParams.set('evidence_page', String(page));
    if (normalizedSentiment === 'all') {
      requestUrl.searchParams.delete('evidence_sentiment');
    } else {
      requestUrl.searchParams.set('evidence_sentiment', normalizedSentiment);
    }
    return requestUrl.pathname + requestUrl.search;
  },
  async fetchDrawerPayload({
    sentiment,
    page,
    append,
    preserveVisibleState = false,
  }) {
    const requestId = this.activeRequestId + 1;
    this.activeRequestId = requestId;
    const preserveContent = Boolean(
      !append && preserveVisibleState && this.cardCount > 0,
    );

    if (append) {
      this.isAppending = true;
    } else {
      this.errorMessage = '';
      this.loading = !preserveContent;
      this.isRefreshing = preserveContent;
      if (!preserveContent) {
        this.cardsHtml = '';
        this.cardCount = 0;
        this.listSummary = '';
      }
    }

    try {
      const response = await fetch(
        this.buildDrawerUrl({
          sentiment,
          page,
        }),
        {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to load supporting evidence.');
      }
      const payload = await response.json();
      if (requestId !== this.activeRequestId) {
        return;
      }
      this.title = String(payload.title || 'Supporting Evidence');
      this.titleFull = String(
        payload.title_full || payload.title || this.title,
      );
      this.subtitle = String(payload.subtitle || '');
      this.listSummary = String(payload.list_summary || '');
      this.chips = Array.isArray(payload.chips) ? payload.chips : [];
      this.activeSentiment = this.normalizeSentiment(payload.active_sentiment);
      const payloadCardsHtml = String(payload.cards_html || '');
      this.cardsHtml = append
        ? this.cardsHtml + payloadCardsHtml
        : payloadCardsHtml;
      this._renderCardsHtml();
      this.cardCount =
        Number(payload.card_count || 0) + (append ? this.cardCount : 0);
      this.totalReviews = Number(payload.total_reviews || 0);
      this.showingReviews = Number(payload.showing_reviews || this.cardCount);
      this.hasMore = Boolean(payload.has_more);
      this.nextPage = payload.next_page ? Number(payload.next_page) : null;
      this.pageSize = Number(payload.page_size || 0);
      this.viewAllUrl = String(payload.view_all_url || this.fallbackUrl);
      this.emptyMessage = String(
        payload.empty_message || 'No matching evidence in this range.',
      );
      if (!append) {
        this.$nextTick(() => {
          if (!preserveContent && this.$refs.closeButton) {
            this.$refs.closeButton.focus();
          }
          if (this.$refs.scrollRegion) {
            this.$refs.scrollRegion.scrollTop = 0;
          }
        });
      }
    } catch (error) {
      if (requestId !== this.activeRequestId) {
        return;
      }
      if (!append && !preserveContent) {
        this.cardsHtml = '';
        this._renderCardsHtml();
        this.cardCount = 0;
        this.errorMessage = 'Unable to load supporting evidence right now.';
      }
      this.hasMore = false;
      this.nextPage = null;
      if (append || preserveContent) {
        this.errorMessage = '';
      }
    } finally {
      if (requestId !== this.activeRequestId) {
        return;
      }
      if (append) {
        this.isAppending = false;
      } else {
        this.loading = false;
        this.isRefreshing = false;
      }
      this._syncLoadMoreObserver();
    }
  },
  _resetState() {
    this.loading = false;
    this.isRefreshing = false;
    this.isAppending = false;
    this.title = 'Supporting Evidence';
    this.titleFull = 'Supporting Evidence';
    this.subtitle = '';
    this.listSummary = '';
    this.chips = [];
    this.activeSentiment = 'all';
    this.cardsHtml = '';
    this._renderCardsHtml();
    this.cardCount = 0;
    this.totalReviews = 0;
    this.showingReviews = 0;
    this.hasMore = false;
    this.nextPage = null;
    this.pageSize = 0;
    this.emptyMessage = 'No matching evidence in this range.';
    this.errorMessage = '';
    this.lastAutoAppendAt = 0;
    this._disconnectLoadMoreObserver();
    if (this.$refs.scrollRegion) {
      this.$refs.scrollRegion.scrollTop = 0;
    }
  },
  close() {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.loading = false;
    this.isRefreshing = false;
    this.isAppending = false;
    this._disconnectLoadMoreObserver();
    this._clearActiveTopicRow();
    this._setScrollLock(false);
    const target = this.lastTriggerElement;
    this.lastTriggerElement = null;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  },
  closeFromEscape(event) {
    if (!this.open) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.close();
  },
  closeOnFocusOutside(event) {
    if (!this.open) {
      return;
    }
    const panel = this.$refs.panel;
    const target = event && event.target;
    if (!panel || !(target instanceof Node)) {
      return;
    }
    if (!panel.contains(target)) {
      this.close();
    }
  },
  _setScrollLock(locked) {
    document.documentElement.classList.toggle('overflow-hidden', locked);
    document.body.classList.toggle('overflow-hidden', locked);
  },
  _renderCardsHtml() {
    const cardsContainer = this.$refs.cardsContainer;
    if (!(cardsContainer instanceof HTMLElement)) {
      return;
    }
    cardsContainer.innerHTML = this.cardsHtml;
  },
  _resolveTopicRow(element) {
    if (!(element instanceof HTMLElement)) {
      return null;
    }
    return element.closest('[data-topic-row]');
  },
  _setActiveTopicRow(rowElement) {
    if (this.activeTopicRow && this.activeTopicRow !== rowElement) {
      this.activeTopicRow.classList.remove(
        'border-l-blue-500',
        'bg-blue-50/60',
        'dark:bg-blue-500/10',
      );
    }
    this.activeTopicRow = rowElement instanceof HTMLElement ? rowElement : null;
    if (!this.activeTopicRow) {
      return;
    }
    this.activeTopicRow.classList.add(
      'border-l-blue-500',
      'bg-blue-50/60',
      'dark:bg-blue-500/10',
    );
  },
  _clearActiveTopicRow() {
    if (!this.activeTopicRow) {
      return;
    }
    this.activeTopicRow.classList.remove(
      'border-l-blue-500',
      'bg-blue-50/60',
      'dark:bg-blue-500/10',
    );
    this.activeTopicRow = null;
  },
  _triggerAutoLoad() {
    if (
      !this.open ||
      !this.hasMore ||
      this.loading ||
      this.isRefreshing ||
      this.isAppending ||
      this.errorMessage
    ) {
      return;
    }
    const now = Date.now();
    if (now - this.lastAutoAppendAt < 350) {
      return;
    }
    this.lastAutoAppendAt = now;
    this.loadMore();
  },
  _syncLoadMoreObserver() {
    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const root = this.$refs.scrollRegion || null;
    if (!this.loadMoreObserver || this.loadMoreObserverRoot !== root) {
      this._disconnectLoadMoreObserver();
      this.loadMoreObserver = new IntersectionObserver(
        (entries) => {
          if (!Array.isArray(entries)) {
            return;
          }
          const visible = entries.some((entry) => entry.isIntersecting);
          if (!visible) {
            return;
          }
          this._triggerAutoLoad();
        },
        {
          root,
          threshold: 0.25,
        },
      );
      this.loadMoreObserverRoot = root;
    }

    this.loadMoreObserver.disconnect();
    if (
      !this.open ||
      !this.hasMore ||
      this.loading ||
      this.isRefreshing ||
      this.isAppending ||
      this.errorMessage
    ) {
      return;
    }
    const loadMoreButton = this.$refs.loadMoreButton;
    if (!(loadMoreButton instanceof HTMLElement)) {
      return;
    }
    this.loadMoreObserver.observe(loadMoreButton);
  },
  _disconnectLoadMoreObserver() {
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
    }
    this.loadMoreObserver = null;
    this.loadMoreObserverRoot = null;
  },
}));

Alpine.data('tooltip', () => ({
  open: false,
  closeTimer: null,
  canHover: window.matchMedia('(hover: hover)').matches,
  clearTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  },
  show() {
    this.clearTimer();
    this.open = true;
  },
  hide(delay = 80) {
    this.clearTimer();
    this.closeTimer = window.setTimeout(() => {
      this.open = false;
      this.closeTimer = null;
    }, delay);
  },
  openFromPointer() {
    if (!this.canHover) {
      return;
    }
    this.show();
  },
  closeFromPointer() {
    if (!this.canHover) {
      return;
    }
    this.hide();
  },
  openFromFocus() {
    this.show();
  },
  closeFromFocus() {
    this.hide(0);
  },
  toggleFromClick() {
    if (this.canHover) {
      this.show();
      return;
    }
    this.clearTimer();
    this.open = !this.open;
  },
  close() {
    this.clearTimer();
    this.open = false;
  },
}));

Alpine.data('collapsibleText', () => ({
  expanded: false,
  overflows: false,
  init() {
    this.$nextTick(() => {
      this._checkOverflow();
    });
  },
  toggle() {
    this.expanded = !this.expanded;
    if (!this.expanded) {
      this.$nextTick(() => {
        this._checkOverflow();
      });
    }
  },
  _checkOverflow() {
    const el = this.$refs.textContent;
    if (el) {
      this.overflows = el.scrollHeight > el.clientHeight;
    }
  },
}));

Alpine.data(
  'reviewRangePicker',
  (initialConfig = false, compareIsCustomLegacy = false) => {
    const config =
      typeof initialConfig === 'object' && initialConfig !== null
        ? initialConfig
        : {
            rangeIsCustom: initialConfig,
            compareIsCustom: compareIsCustomLegacy,
          };

    const allowCustom = config.allowCustom !== false;
    const allowComparison = config.allowComparison !== false;
    const rangeIsCustom = allowCustom && Boolean(config.rangeIsCustom);
    const compareIsCustom = allowComparison && Boolean(config.compareIsCustom);

    return {
      allowCustom,
      allowComparison,
      open: false,
      showCustom: rangeIsCustom,
      showCompare: compareIsCustom,
      compareMode: compareIsCustom ? 'custom' : 'auto',
      compareAutoLabel: '',
      compareCustomLabel: '',
      _displayFormatter: new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      _monthDayFormatter: new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      _parseDateToUtcMs(value) {
        if (!value) {
          return null;
        }
        const parts = value.split('-').map((part) => Number(part));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
          return null;
        }
        const [year, month, day] = parts;
        return Date.UTC(year, month - 1, day);
      },
      _parseDateParts(value) {
        if (!value) {
          return null;
        }
        const parts = value.split('-').map((part) => Number(part));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
          return null;
        }
        const [year, month, day] = parts;
        return { year, month, day };
      },
      _formatUtcMs(ms) {
        const date = new Date(ms);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      },
      _formatDisplayValue(value) {
        const parts = this._parseDateParts(value);
        if (!parts) {
          return '';
        }
        const date = new Date(parts.year, parts.month - 1, parts.day);
        if (Number.isNaN(date.getTime())) {
          return '';
        }
        return this._displayFormatter.format(date);
      },
      _formatRangeLabel(startValue, endValue) {
        const startParts = this._parseDateParts(startValue);
        const endParts = this._parseDateParts(endValue);
        if (!startParts || !endParts) {
          return '';
        }
        const startDate = new Date(
          startParts.year,
          startParts.month - 1,
          startParts.day,
        );
        const endDate = new Date(
          endParts.year,
          endParts.month - 1,
          endParts.day,
        );
        if (
          Number.isNaN(startDate.getTime()) ||
          Number.isNaN(endDate.getTime())
        ) {
          return '';
        }
        if (
          startDate.getFullYear() === endDate.getFullYear() &&
          startDate.getMonth() === endDate.getMonth() &&
          startDate.getDate() === endDate.getDate()
        ) {
          return this._displayFormatter.format(startDate);
        }
        if (startDate.getFullYear() === endDate.getFullYear()) {
          if (startDate.getMonth() === endDate.getMonth()) {
            return `${this._monthDayFormatter.format(startDate)} - ${endDate.getDate()}, ${endDate.getFullYear()}`;
          }
          return `${this._monthDayFormatter.format(startDate)} - ${this._monthDayFormatter.format(endDate)}, ${endDate.getFullYear()}`;
        }
        return `${this._displayFormatter.format(startDate)} - ${this._displayFormatter.format(endDate)}`;
      },
      _updateCompareCustomLabel() {
        if (!this.allowComparison) {
          return;
        }
        const startValue = this.$refs.compareStartValue?.value || '';
        const endValue = this.$refs.compareEndValue?.value || '';
        this.compareCustomLabel = this._formatRangeLabel(startValue, endValue);
      },
      _setCompareValues(startValue, endValue) {
        if (!this.allowComparison) {
          return;
        }

        if (this.$refs.compareStartValue) {
          this.$refs.compareStartValue.value = startValue;
          this.$refs.compareStartValue.dispatchEvent(new Event('change'));
        }

        if (this.$refs.compareEndValue) {
          this.$refs.compareEndValue.value = endValue;
          this.$refs.compareEndValue.dispatchEvent(new Event('change'));
        }

        if (this.$refs.compareStartDisplay) {
          this.$refs.compareStartDisplay.value =
            this._formatDisplayValue(startValue);
        }

        if (this.$refs.compareEndDisplay) {
          this.$refs.compareEndDisplay.value =
            this._formatDisplayValue(endValue);
        }
      },
      _updateAutoCompareFromRange() {
        if (!this.allowComparison) {
          return;
        }
        const rangeStartMs = this._parseDateToUtcMs(
          this.$refs.rangeStartValue?.value || '',
        );
        const rangeEndMs = this._parseDateToUtcMs(
          this.$refs.rangeEndValue?.value || '',
        );

        if (rangeStartMs === null || rangeEndMs === null) {
          this._setCompareValues('', '');
          this.compareAutoLabel = '';
          return;
        }
        if (rangeEndMs < rangeStartMs) {
          this._setCompareValues('', '');
          this.compareAutoLabel = '';
          return;
        }

        const dayMs = 24 * 60 * 60 * 1000;
        const durationDays =
          Math.floor((rangeEndMs - rangeStartMs) / dayMs) + 1;
        const compareEndMs = rangeStartMs - dayMs;
        const compareStartMs = rangeStartMs - durationDays * dayMs;
        const compareStartValue = this._formatUtcMs(compareStartMs);
        const compareEndValue = this._formatUtcMs(compareEndMs);
        this._setCompareValues(compareStartValue, compareEndValue);
        this.compareAutoLabel = this._formatRangeLabel(
          compareStartValue,
          compareEndValue,
        );
      },
      refreshAutoLabel() {
        if (!this.allowComparison || this.compareMode !== 'auto') {
          return;
        }
        const startValue = this.$refs.compareStartValue?.value || '';
        const endValue = this.$refs.compareEndValue?.value || '';
        const label = this._formatRangeLabel(startValue, endValue);
        if (label) {
          this.compareAutoLabel = label;
          return;
        }
        const rangeStartValue = this.$refs.rangeStartValue?.value || '';
        const rangeEndValue = this.$refs.rangeEndValue?.value || '';
        const rangeLabel = this._formatRangeLabel(
          rangeStartValue,
          rangeEndValue,
        );
        this.compareAutoLabel = rangeLabel || '';
      },
      init() {
        if (!this.allowComparison) {
          return;
        }
        this.refreshAutoLabel();
        this._updateCompareCustomLabel();
        const handleRangeChange = () => {
          this.compareMode = 'auto';
          this._updateAutoCompareFromRange();
          this.refreshAutoLabel();
        };
        const handleCompareChange = () => {
          this._updateCompareCustomLabel();
          if (this.compareMode === 'auto') {
            this.refreshAutoLabel();
          }
        };

        if (this.$refs.rangeStartValue) {
          this.$refs.rangeStartValue.addEventListener(
            'change',
            handleRangeChange,
          );
        }
        if (this.$refs.rangeEndValue) {
          this.$refs.rangeEndValue.addEventListener(
            'change',
            handleRangeChange,
          );
        }
        if (this.$refs.compareStartValue) {
          this.$refs.compareStartValue.addEventListener(
            'change',
            handleCompareChange,
          );
        }
        if (this.$refs.compareEndValue) {
          this.$refs.compareEndValue.addEventListener(
            'change',
            handleCompareChange,
          );
        }
      },
      toggleOpen() {
        this.open = !this.open;
      },
      toggleCustom() {
        if (!this.allowCustom) {
          return;
        }
        this.showCustom = !this.showCustom;
      },
      toggleCompareEditor() {
        if (!this.allowComparison) {
          return;
        }
        this.showCompare = !this.showCompare;
        if (this.showCompare) {
          this.compareMode = 'custom';
          this._updateCompareCustomLabel();
        } else if (this.compareMode === 'custom') {
          this._updateCompareCustomLabel();
        }
      },
      resetCompareToAuto() {
        if (!this.allowComparison) {
          return;
        }
        this.compareMode = 'auto';
        this._updateAutoCompareFromRange();
      },
    };
  },
);

Alpine.data('reviewIntake', () => ({
  rawText: '',
  files: [],
  fileCount: 0,
  fileSuffix: '',
  showLimit: false,
  displayFiles: [],
  setFiles(event) {
    this.files = Array.from(
      (event && event.target && event.target.files) || [],
    );
    this.refreshList();
  },
  removeFile(index) {
    this.files.splice(index, 1);
    this.refreshInput();
    this.refreshList();
  },
  refreshInput() {
    const input = this.$refs.reviewIntakeFiles;
    if (!input) {
      return;
    }
    const dataTransfer = new DataTransfer();
    this.files.forEach((file) => dataTransfer.items.add(file));
    input.files = dataTransfer.files;
  },
  refreshList() {
    this.fileCount = this.files.length;
    this.fileSuffix = this.fileCount === 1 ? '' : 's';
    this.showLimit = this.fileCount > 25;
    this.displayFiles = this.files.slice(0, 25).map((file) => ({
      name: file.name,
      sizeLabel: String(Math.ceil(file.size / 1024)) + ' KB',
    }));
  },
}));

const DEFAULT_REVIEW_SORT_OPTIONS = [
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
];

const filterSearchUtils = {
  normalizeLabelText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  },

  isSubsequence(query, text) {
    if (!query) {
      return true;
    }
    if (!text) {
      return false;
    }
    let queryIndex = 0;
    for (let index = 0; index < text.length; index += 1) {
      if (text[index] === query[queryIndex]) {
        queryIndex += 1;
        if (queryIndex === query.length) {
          return true;
        }
      }
    }
    return false;
  },

  matchesLabelQuery(query, searchableText) {
    const normalizedQuery = this.normalizeLabelText(query);
    const normalizedSearchableText = this.normalizeLabelText(searchableText);
    if (!normalizedQuery) {
      return true;
    }
    if (!normalizedSearchableText) {
      return false;
    }
    if (normalizedSearchableText.includes(normalizedQuery)) {
      return true;
    }
    const queryTokens = normalizedQuery.split(' ').filter(Boolean);
    if (!queryTokens.length) {
      return true;
    }
    const searchableTokens = normalizedSearchableText
      .split(' ')
      .filter(Boolean);
    return queryTokens.every((token) => {
      if (
        normalizedSearchableText.includes(token) ||
        this.isSubsequence(token, normalizedSearchableText)
      ) {
        return true;
      }
      return searchableTokens.some(
        (searchableToken) =>
          searchableToken.includes(token) ||
          this.isSubsequence(token, searchableToken),
      );
    });
  },
};

const searchableMultiSelect = {
  selectionKey(value) {
    return filterSearchUtils.normalizeLabelText(value);
  },

  normalizeStringOptions(options) {
    const source = Array.isArray(options) ? options : [];
    const normalized = [];
    const seen = new Set();
    source.forEach((option) => {
      const value = String(option || '').trim();
      const key = this.selectionKey(value);
      if (!value || !key || seen.has(key)) {
        return;
      }
      seen.add(key);
      normalized.push({
        key,
        value,
        label: value,
      });
    });
    return normalized;
  },

  normalizeObjectOptions(options) {
    const source = Array.isArray(options) ? options : [];
    const normalized = [];
    const seen = new Set();
    source.forEach((option) => {
      if (!option || typeof option !== 'object') {
        return;
      }
      const label = String(
        option.label || option.value || option.key || '',
      ).trim();
      const value = this.selectionKey(option.value || option.key || label);
      const key = this.selectionKey(option.key || value || label);
      if (!label || !value || !key || key === 'all' || seen.has(key)) {
        return;
      }
      seen.add(key);
      normalized.push({
        key,
        value,
        label,
      });
    });
    return normalized;
  },

  findOption(options, candidateValue) {
    const key = this.selectionKey(candidateValue);
    const source = Array.isArray(options) ? options : [];
    return source.find((option) => option.key === key) || null;
  },

  isSelected(selectedValues, candidateValue) {
    const candidateKey = this.selectionKey(candidateValue);
    const source = Array.isArray(selectedValues) ? selectedValues : [];
    return source.some(
      (selectedValue) => this.selectionKey(selectedValue) === candidateKey,
    );
  },

  selectedChips(selectedValues, options) {
    const source = Array.isArray(selectedValues) ? selectedValues : [];
    const chips = [];
    const seen = new Set();
    source.forEach((selectedValue) => {
      const key = this.selectionKey(selectedValue);
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      const option = this.findOption(options, selectedValue);
      const fallbackValue = String(selectedValue || '').trim();
      chips.push({
        key,
        value: option?.value || fallbackValue,
        label: option?.label || fallbackValue,
      });
    });
    return chips;
  },

  filterOptions(options, query) {
    const source = Array.isArray(options) ? options : [];
    if (!String(query || '').trim()) {
      return source;
    }
    return source.filter((option) =>
      filterSearchUtils.matchesLabelQuery(
        query,
        `${option.label || ''} ${option.value || ''} ${option.key || ''}`,
      ),
    );
  },

  buttonLabel(selectedValues, options, { emptyLabel = 'Topics' } = {}) {
    const source = Array.isArray(selectedValues) ? selectedValues : [];
    if (!source.length) {
      return emptyLabel;
    }
    if (source.length === 1) {
      const option = this.findOption(options, source[0]);
      return option?.label || String(source[0] || '').trim() || emptyLabel;
    }
    return `${source.length} topics selected`;
  },
};

const singleSelectDropdown = {
  normalizeOptions(options, fallbackOptions = []) {
    const sourceOptions = Array.isArray(options) ? options : [];
    const normalized = [];
    const seen = new Set();
    sourceOptions.forEach((option) => {
      if (!option || typeof option !== 'object') {
        return;
      }
      const key = String(option.key || '')
        .trim()
        .toLowerCase();
      const label = String(option.label || '').trim();
      if (!key || !label || seen.has(key)) {
        return;
      }
      seen.add(key);
      normalized.push({ key, label });
    });
    if (normalized.length) {
      return normalized;
    }

    const fallbackSource = Array.isArray(fallbackOptions)
      ? fallbackOptions
      : [];
    return fallbackSource.map((option) => ({
      key: String(option.key || '')
        .trim()
        .toLowerCase(),
      label: String(option.label || '').trim(),
    }));
  },

  allowedKeys(options) {
    const sourceOptions = Array.isArray(options) ? options : [];
    return sourceOptions.map((option) => option.key);
  },

  defaultKey(options, fallbackKey = '') {
    const sourceOptions = Array.isArray(options) ? options : [];
    if (sourceOptions.length) {
      return sourceOptions[0].key;
    }
    return String(fallbackKey || '')
      .trim()
      .toLowerCase();
  },

  label(options, selectedKey, fallbackLabel = 'Select') {
    const sourceOptions = Array.isArray(options) ? options : [];
    const selected = String(selectedKey || '')
      .trim()
      .toLowerCase();
    const activeOption = sourceOptions.find(
      (option) => option.key === selected,
    );
    if (activeOption) {
      return activeOption.label;
    }
    return fallbackLabel;
  },

  resolveValue(nextValue, options, fallbackKey = '') {
    const sourceOptions = Array.isArray(options) ? options : [];
    const defaultKey = this.defaultKey(sourceOptions, fallbackKey);
    const candidate = String(nextValue || defaultKey)
      .trim()
      .toLowerCase();
    const allowedKeys = this.allowedKeys(sourceOptions);
    if (allowedKeys.includes(candidate)) {
      return candidate;
    }
    return defaultKey;
  },
};

const asyncPaginatedList = {
  buildUrl(pathname, params) {
    const query = params instanceof URLSearchParams ? params.toString() : '';
    return query ? `${pathname}?${query}` : pathname;
  },

  renderListHtml(
    container,
    html,
    { append = false, initializeTree = false } = {},
  ) {
    if (!(container instanceof HTMLElement)) {
      return;
    }
    const markup = String(html || '');
    if (!append) {
      container.innerHTML = markup;
      if (initializeTree) {
        Alpine.initTree(container);
      }
      return;
    }

    if (!markup) {
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = markup;
    const appendedNodes = Array.from(template.content.childNodes);
    container.appendChild(template.content);
    if (!initializeTree) {
      return;
    }
    appendedNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        Alpine.initTree(node);
      }
    });
  },

  async fetchPage(
    component,
    {
      page,
      append = false,
      supportsAppend = false,
      buildParams,
      applyPayload,
      buildRequestContext,
      syncUrl,
      onError,
    },
  ) {
    const requestId = component.activeRequestId + 1;
    component.activeRequestId = requestId;
    const requestContext =
      typeof buildRequestContext === 'function' ? buildRequestContext() : {};

    if (append && supportsAppend) {
      component.isLoadingMore = true;
    } else {
      component.isLoading = true;
      component.isLoadingMore = false;
      if (component.fetchController) {
        component.fetchController.abort();
      }
    }

    const controller = new AbortController();
    component.fetchController = controller;
    const params =
      typeof buildParams === 'function'
        ? buildParams(page)
        : new URLSearchParams();
    const url = this.buildUrl(window.location.pathname, params);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`Paginated request failed with ${response.status}`);
      }
      const payload = await response.json();
      if (requestId !== component.activeRequestId) {
        return;
      }
      applyPayload(payload, {
        page,
        append: append && supportsAppend,
        requestContext,
      });
      if (!append && typeof syncUrl === 'function') {
        syncUrl(payload, { page, requestContext });
      }
    } catch (error) {
      if (requestId !== component.activeRequestId) {
        return;
      }
      if (error && error.name === 'AbortError') {
        return;
      }
      if (typeof onError === 'function') {
        onError(error, {
          page,
          append: append && supportsAppend,
          requestContext,
        });
      }
    } finally {
      if (requestId !== component.activeRequestId) {
        return;
      }
      if (component.fetchController === controller) {
        component.fetchController = null;
      }
      component.isLoading = false;
      component.isLoadingMore = false;
    }
  },

  loadNextPage(
    component,
    { supportsAppend = false, hasMore = false, nextPage = 1 },
  ) {
    if (
      !supportsAppend ||
      component.isLoading ||
      component.isLoadingMore ||
      !hasMore
    ) {
      return;
    }
    component._fetchPage(nextPage, { append: true });
  },
};

const reviewDrawerFactory = () => ({
  drawerOpen: false,
  activeReview: null,
  drawerLoading: false,
  reviewDetailData: {},

  init() {
    this._loadReviewDetailData();
  },

  _loadReviewDetailData() {
    const el = document.getElementById('reviews-detail-data');
    if (!el) {
      return;
    }
    try {
      this.reviewDetailData = JSON.parse(el.textContent || '{}');
    } catch (_e) {
      this.reviewDetailData = {};
    }
  },

  openReviewDrawer(reviewId) {
    const id = String(reviewId || '');
    const review = this.reviewDetailData[id];
    this.activeReview = review || null;
    this.drawerOpen = true;
  },

  closeDrawer() {
    if (!this.drawerOpen) {
      return;
    }
    this.drawerOpen = false;
    setTimeout(() => {
      this.activeReview = null;
    }, 500);
  },
});

Alpine.data('reviewDrawer', reviewDrawerFactory);

Alpine.data('reviewsPage', () => ({
  ...reviewDrawerFactory(),
  searchQuery: '',
  selectedTopics: [],
  topicOptions: [],
  topicSearch: '',
  topicDropdownOpen: false,
  activeTopicIndex: 0,
  sentiment: 'all',
  sortDropdownOpen: false,
  sort: 'newest',
  sortOptions: [...DEFAULT_REVIEW_SORT_OPTIONS],
  _sentimentCounts: { all: 0, negative: 0, positive: 0, neutral: 0 },
  get sentimentOptions() {
    return [
      {
        key: 'all',
        label: 'All',
        count: this._sentimentCounts.all,
        active: this.sentiment === 'all',
        show_count: false,
      },
      {
        key: 'negative',
        label: 'Negative',
        count: this._sentimentCounts.negative,
        active: this.sentiment === 'negative',
        show_count: true,
      },
      {
        key: 'positive',
        label: 'Positive',
        count: this._sentimentCounts.positive,
        active: this.sentiment === 'positive',
        show_count: true,
      },
      {
        key: 'neutral',
        label: 'Neutral',
        count: this._sentimentCounts.neutral,
        active: this.sentiment === 'neutral',
        show_count: true,
      },
    ];
  },
  hasResults: true,
  isLoading: false,
  hasError: false,
  errorMessage: '',
  hasMore: false,
  currentPage: 1,
  searchDebounceTimer: null,
  searchRevision: 0,
  fetchController: null,
  activeRequestId: 0,
  baseParams: new URLSearchParams(),
  rootElement: null,
  reviewsListElement: null,
  summaryTextElement: null,

  init() {
    this.rootElement =
      this.$root instanceof HTMLElement
        ? this.$root
        : document.getElementById('reviews');
    this._cacheDomRefs();

    const initialSentiment = String(
      this.$root?.dataset?.initialSentiment || 'all',
    ).toLowerCase();
    if (['all', 'positive', 'negative', 'neutral'].includes(initialSentiment)) {
      this.sentiment = initialSentiment;
    }

    const searchInput = this.$refs.searchInput;
    if (searchInput instanceof HTMLInputElement) {
      this.searchQuery = String(searchInput.value || '').trim();
    }

    this.sortOptions = singleSelectDropdown.normalizeOptions(
      this._parseJsonScript('reviews-sort-options-data', []),
      DEFAULT_REVIEW_SORT_OPTIONS,
    );
    const defaultSortKey = singleSelectDropdown.defaultKey(
      this.sortOptions,
      'newest',
    );
    this.sort = defaultSortKey;
    const initialSort = String(
      this.$root?.dataset?.initialSort || defaultSortKey,
    )
      .trim()
      .toLowerCase();
    if (
      singleSelectDropdown.allowedKeys(this.sortOptions).includes(initialSort)
    ) {
      this.sort = initialSort;
    }

    this.$nextTick(() => {
      this.$dispatch('options-update', {
        name: 'sort',
        options: this.sortOptions,
        value: this.sort,
      });
    });

    const topicSuggestions = this._parseJsonScript(
      'reviews-topic-suggestions-data',
      [],
    );
    this.selectedTopics = this._parseJsonScript(
      'reviews-selected-topics-data',
      [],
    );
    this.selectedTopics = this._dedupeTopics(this.selectedTopics);
    this.topicOptions = searchableMultiSelect.normalizeStringOptions([
      ...topicSuggestions,
      ...this.selectedTopics,
    ]);

    this.$nextTick(() => {
      this.$dispatch('options-update', {
        name: 'topics',
        options: this.topicOptions.map((o) => ({
          key: o.value || o.key,
          label: o.label,
        })),
        value: this.selectedTopics || [],
      });
    });

    this._loadBaseParams();
    this._loadReviewDetailData();

    // Populate initial sentiment counts from server-rendered data
    const sentimentFiltersData = this._parseJsonScript(
      'reviews-sentiment-filters-data',
      [],
    );
    if (Array.isArray(sentimentFiltersData)) {
      sentimentFiltersData.forEach((sf) => {
        if (sf.key && typeof sf.count === 'number') {
          this._sentimentCounts[sf.key] = sf.count;
        }
      });
    }

    const list = this.reviewsListElement;
    if (list instanceof HTMLElement) {
      this.currentPage = Number.parseInt(list.dataset.page || '1', 10) || 1;
      this.hasMore = list.dataset.hasMore === '1';
      this.hasResults = list.children.length > 0;
    } else {
      this.hasResults = false;
    }

    // Force full reload when restored from bfcache to avoid stale DOM after
    // replaceState URL updates from filter changes.
    window.addEventListener(
      'pageshow',
      function (event) {
        if (event.persisted) {
          window.location.reload();
        }
      },
      { once: true },
    );
  },

  _parseJsonScript(id, fallbackValue) {
    const script = document.getElementById(id);
    if (!(script instanceof HTMLScriptElement)) {
      return fallbackValue;
    }
    try {
      const parsed = JSON.parse(script.textContent || '');
      return parsed ?? fallbackValue;
    } catch (_error) {
      return fallbackValue;
    }
  },

  _cacheDomRefs() {
    if (!(this.rootElement instanceof HTMLElement)) {
      return;
    }
    const list = this.rootElement.querySelector('[x-ref="reviewsList"]');
    if (list instanceof HTMLElement) {
      this.reviewsListElement = list;
    }
    const summary = this.rootElement.querySelector('[x-ref="summaryText"]');
    if (summary instanceof HTMLElement) {
      this.summaryTextElement = summary;
    }
  },

  _loadBaseParams() {
    this.baseParams = new URLSearchParams();
    if (!(this.rootElement instanceof HTMLElement)) {
      return;
    }
    const params = this.rootElement.querySelectorAll(
      '[data-reviews-base-param]',
    );
    params.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) {
        return;
      }
      if (!input.name || input.value === '') {
        return;
      }
      this.baseParams.append(String(input.name), String(input.value));
    });
    this._stripManagedReviewParams(this.baseParams);
  },

  _stripManagedReviewParams(params) {
    if (!(params instanceof URLSearchParams)) {
      return;
    }
    [
      'reviews_q',
      'reviews_search',
      'reviews_topic',
      'reviews_sentiment',
      'reviews_sort',
      'reviews_page',
    ].forEach((key) => params.delete(key));
  },

  topicButtonLabel() {
    return searchableMultiSelect.buttonLabel(
      this.selectedTopics,
      this.topicOptions,
      { emptyLabel: 'Topics' },
    );
  },

  sortButtonLabel() {
    return singleSelectDropdown.label(this.sortOptions, this.sort, 'Sort');
  },

  toggleSortDropdown() {
    const nextState = !this.sortDropdownOpen;
    if (nextState) {
      this.closeTopicDropdown();
    }
    this.sortDropdownOpen = nextState;
  },

  closeSortDropdown() {
    this.sortDropdownOpen = false;
  },

  setSort(nextSort) {
    const nextValue = singleSelectDropdown.resolveValue(
      nextSort,
      this.sortOptions,
      'newest',
    );
    const hasChanged = this.sort !== nextValue;
    this.sort = nextValue;
    this.closeSortDropdown();
    if (hasChanged) {
      this.applyFilters();
    }
  },

  handleSelectChange(detail) {
    if (detail.name === 'sort') {
      this.setSort(detail.value);
    }
    if (detail.name === 'topics') {
      this.selectedTopics = Array.isArray(detail.value) ? detail.value : [];
      this.applyFilters();
    }
  },

  _dedupeTopics(topics) {
    const cleaned = [];
    const seen = new Set();
    topics.forEach((topic) => {
      const value = String(topic || '').trim();
      if (!value) {
        return;
      }
      const key = searchableMultiSelect.selectionKey(value);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      cleaned.push(value);
    });
    return cleaned;
  },

  _ensureTopicOption(topic) {
    const value = String(topic || '').trim();
    if (!value) {
      return;
    }
    if (searchableMultiSelect.findOption(this.topicOptions, value)) {
      return;
    }
    this.topicOptions = searchableMultiSelect.normalizeStringOptions([
      ...this.topicOptions.map((option) => option.value),
      value,
    ]);
  },

  filteredTopicOptions() {
    return searchableMultiSelect.filterOptions(
      this.topicOptions,
      this.topicSearch,
    );
  },

  isTopicSelected(topic) {
    return searchableMultiSelect.isSelected(this.selectedTopics, topic);
  },

  toggleTopicDropdown() {
    if (!this.topicDropdownOpen) {
      this.closeSortDropdown();
    }
    this.topicDropdownOpen = !this.topicDropdownOpen;
    if (this.topicDropdownOpen) {
      this.activeTopicIndex = 0;
      this.$nextTick(() => {
        const topicSearchInput = this.$refs.topicSearchInput;
        if (topicSearchInput instanceof HTMLInputElement) {
          topicSearchInput.focus();
        }
      });
    } else {
      this.topicSearch = '';
    }
  },

  closeTopicDropdown() {
    this.topicDropdownOpen = false;
    this.topicSearch = '';
    this.activeTopicIndex = 0;
  },

  toggleTopic(topic) {
    const value = String(topic || '').trim();
    if (!value) {
      return;
    }
    if (!this.isTopicSelected(value)) {
      this._ensureTopicOption(value);
    }
    if (this.isTopicSelected(value)) {
      this.selectedTopics = this.selectedTopics.filter(
        (item) =>
          searchableMultiSelect.selectionKey(item) !==
          searchableMultiSelect.selectionKey(value),
      );
    } else {
      this.selectedTopics = [...this.selectedTopics, value];
    }
    this.selectedTopics = this._dedupeTopics(this.selectedTopics);
    this.topicSearch = '';
    this.activeTopicIndex = 0;
    this.applyFilters();
  },

  toggleTopicChipFilter(topic) {
    const value = String(topic || '').trim();
    if (!value) return;
    this.$dispatch('select-command', {
      name: 'topics',
      action: 'remove',
      key: value,
    });
  },

  selectedTopicChips() {
    return searchableMultiSelect.selectedChips(
      this.selectedTopics,
      this.topicOptions,
    );
  },

  removeTopic(topic) {
    const value = String(topic || '').trim();
    if (!value) return;
    this.$dispatch('select-command', {
      name: 'topics',
      action: 'remove',
      key: value,
    });
  },

  focusNextTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      this.activeTopicIndex = 0;
      return;
    }
    this.activeTopicIndex = Math.min(
      this.activeTopicIndex + 1,
      options.length - 1,
    );
  },

  focusPreviousTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      this.activeTopicIndex = 0;
      return;
    }
    this.activeTopicIndex = Math.max(this.activeTopicIndex - 1, 0);
  },

  selectActiveTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      return;
    }
    const selected = options[this.activeTopicIndex];
    if (selected) {
      this.toggleTopic(selected.value);
    }
  },

  setSentiment(nextSentiment) {
    const value = String(nextSentiment || 'all')
      .trim()
      .toLowerCase();
    const nextValue = ['all', 'positive', 'negative', 'neutral'].includes(value)
      ? value
      : 'all';
    if (this.sentiment === nextValue) {
      return;
    }
    this.sentiment = nextValue;
    this.applyFilters();
  },

  applySentimentChipFilter(sentiment) {
    const value = String(sentiment || '')
      .trim()
      .toLowerCase();
    if (!['positive', 'negative', 'neutral'].includes(value)) {
      return;
    }
    this.setSentiment(value);
  },

  onSearchInput() {
    this.searchRevision += 1;
    if (this.searchDebounceTimer) {
      window.clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = window.setTimeout(() => {
      this.applyFilters();
    }, 280);
  },

  hasActiveFilters() {
    const defaultSortKey = singleSelectDropdown.defaultKey(
      this.sortOptions,
      'newest',
    );
    return Boolean(
      this.searchQuery.trim() ||
        this.selectedTopics.length ||
        this.sentiment !== 'all' ||
        this.sort !== defaultSortKey,
    );
  },

  clearAll() {
    this.searchQuery = '';
    this.sentiment = 'all';
    this.topicSearch = '';
    this.closeTopicDropdown();
    this.closeSortDropdown();
    this.$dispatch('select-command', { name: 'topics', action: 'clear' });
    this.$dispatch('select-command', {
      name: 'sort',
      action: 'set',
      value: singleSelectDropdown.defaultKey(this.sortOptions, 'newest'),
    });
    this.applyFilters();
  },

  _buildParams(page = 1) {
    const params = new URLSearchParams(this.baseParams.toString());
    this._stripManagedReviewParams(params);
    const search = String(this.searchQuery || '').trim();
    if (search) {
      params.set('reviews_search', search);
    }
    this.selectedTopics.forEach((topic) => {
      params.append('reviews_topic', topic);
    });
    if (this.sentiment !== 'all') {
      params.set('reviews_sentiment', this.sentiment);
    }
    const defaultSortKey = singleSelectDropdown.defaultKey(
      this.sortOptions,
      'newest',
    );
    if (this.sort !== defaultSortKey) {
      params.set('reviews_sort', this.sort);
    }
    if (page > 1) {
      params.set('reviews_page', String(page));
    }
    return params;
  },

  _updateSummary(summary) {
    this._cacheDomRefs();
    const filteredCount = Number(summary.filtered_count || 0);
    const totalInRange = Number(summary.total_in_range || 0);
    const summaryText = `Showing ${filteredCount.toLocaleString()} of ${totalInRange.toLocaleString()} reviews`;

    const target = this.summaryTextElement;
    if (target instanceof HTMLElement) {
      target.textContent = summaryText;
    }

    const pageSummary = document.querySelector('[data-reviews-page-summary]');
    if (pageSummary instanceof HTMLElement) {
      pageSummary.textContent = summaryText;
    }
  },

  _syncFiltersFromPayload(filters, requestContext = null) {
    if (!filters || typeof filters !== 'object') {
      return;
    }

    if (Array.isArray(filters.topics)) {
      this.selectedTopics = this._dedupeTopics(filters.topics);
      const topicSuggestions = Array.isArray(filters.topic_suggestions)
        ? filters.topic_suggestions
        : this.topicOptions.map((option) => option.value);
      this.topicOptions = searchableMultiSelect.normalizeStringOptions([
        ...topicSuggestions,
        ...this.selectedTopics,
      ]);
      this.$nextTick(() => {
        this.$dispatch('options-update', {
          name: 'topics',
          options: this.topicOptions.map((o) => ({
            key: o.value || o.key,
            label: o.label,
          })),
          value: this.selectedTopics || [],
        });
      });
    }

    if (typeof filters.search_query === 'string') {
      const canSyncSearch =
        !requestContext ||
        requestContext.searchRevision === this.searchRevision;
      if (canSyncSearch) {
        this.searchQuery = filters.search_query;
      }
    }

    const nextSentiment = String(filters.sentiment || '')
      .trim()
      .toLowerCase();
    if (['all', 'positive', 'negative', 'neutral'].includes(nextSentiment)) {
      this.sentiment = nextSentiment;
    }

    if (Array.isArray(filters.sentiment_filters)) {
      filters.sentiment_filters.forEach((sf) => {
        if (sf.key && typeof sf.count === 'number') {
          this._sentimentCounts[sf.key] = sf.count;
        }
      });
    }

    const nextSort = String(filters.sort || '')
      .trim()
      .toLowerCase();
    if (singleSelectDropdown.allowedKeys(this.sortOptions).includes(nextSort)) {
      this.sort = nextSort;
    }
  },

  _replaceListHtml(html) {
    this._cacheDomRefs();
    const list = this.reviewsListElement;
    asyncPaginatedList.renderListHtml(list, html);
  },

  _syncUrlFromFilters() {
    const params = this._buildParams(1);
    const query = params.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}#reviews`
      : `${window.location.pathname}#reviews`;
    window.history.replaceState({}, '', nextUrl);
  },

  async _fetchPage(page, { append = false } = {}) {
    return asyncPaginatedList.fetchPage(this, {
      page,
      append,
      supportsAppend: false,
      buildParams: (nextPage) => this._buildParams(nextPage),
      buildRequestContext: () => ({
        searchRevision: this.searchRevision,
      }),
      applyPayload: (payload, { page: requestedPage, requestContext }) => {
        this.hasError = false;
        this.errorMessage = '';
        this._syncFiltersFromPayload(payload?.filters || {}, requestContext);
        this._replaceListHtml(payload.rows_html);
        this.currentPage = Number(payload?.pagination?.page || requestedPage);
        this.hasMore = Boolean(payload?.pagination?.has_more);
        this.hasResults = !Boolean(payload?.empty);
        this._updateSummary(payload?.showing_summary || {});
      },
      syncUrl: () => this._syncUrlFromFilters(),
      onError: (error) => {
        this.hasError = true;
        this.errorMessage =
          (error && error.message) || 'Couldn’t load reviews. Try again.';
        // eslint-disable-next-line no-console
        console.error('Failed to load reviews.', error);
      },
    });
  },

  retry() {
    this.hasError = false;
    this.errorMessage = '';
    this._fetchPage(this.currentPage || 1, { append: false });
  },

  applyFilters() {
    // Debounce: multiple calls in the same tick (e.g. clearAll dispatching
    // commands that each trigger handleSelectChange → applyFilters) collapse
    // into a single fetch.
    if (this._applyPending) return;
    this._applyPending = true;
    var self = this;
    queueMicrotask(function () {
      self._applyPending = false;
      self.closeSortDropdown();
      self.currentPage = 1;
      self.hasMore = false;
      self._fetchPage(1, { append: false });
    });
  },
}));

Alpine.data('actionItemsPage', () => ({
  searchQuery: '',
  status: 'all',
  priority: 'all',
  selectedTopics: [],
  topicSearch: '',
  activeTopicIndex: 0,
  priorityDropdownOpen: false,
  topicDropdownOpen: false,
  statusTabs: [],
  priorityOptions: [
    { key: 'all', label: 'All priorities' },
    { key: 'high', label: 'High Priority' },
    { key: 'medium', label: 'Medium Priority' },
    { key: 'low', label: 'Low Priority' },
  ],
  topicOptions: [],
  summary: {
    total: 0,
    open: 0,
    in_progress: 0,
    completed: 0,
    completion_label: '0/0',
    progress_percent: 0,
    priority_summary: [],
  },
  pagination: {
    page: 1,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    total_matching: 0,
  },
  emptyState: {
    title: 'No action items yet',
    description: '',
  },
  hasResults: false,
  isLoading: false,
  hasError: false,
  errorMessage: '',
  searchDebounceTimer: null,
  searchRevision: 0,
  fetchController: null,
  activeRequestId: 0,
  baseParams: new URLSearchParams(),
  rootElement: null,
  rowsListElement: null,
  pageSummaryElement: null,
  completionLabelElement: null,
  summaryBreakdownElement: null,
  progressBarElement: null,
  prioritySummaryElement: null,
  // statusTabsElement removed — underline_tabs.html renders reactively from statusTabs
  showingSummaryElement: null,
  emptyTitleElement: null,
  emptyDescriptionElement: null,

  // Evidence drawer state
  evidenceDrawerOpen: false,
  activeActionItem: null,
  evidenceLoading: false,
  evidenceHtml: '',
  evidenceError: '',
  actionItemDetailData: {},

  init() {
    this.rootElement =
      this.$root instanceof HTMLElement
        ? this.$root
        : document.getElementById('action-items');
    this._cacheDomRefs();
    this._loadInitialState();
    this._loadBaseParams();
    this._updateHeaderSummary();
    this._renderPrioritySummary();
    this._renderStatusTabs();

    this.$nextTick(() => {
      this.$dispatch('options-update', {
        name: 'priority',
        options: this.priorityOptions,
        value: this.priority,
      });
      this.$dispatch('options-update', {
        name: 'topics',
        options: this.topicOptions.map((o) => ({
          key: o.value || o.key,
          label: o.label,
        })),
        value: this.selectedTopics || [],
      });
    });
  },

  _parseJsonScript(id, fallbackValue) {
    const script = document.getElementById(id);
    if (!(script instanceof HTMLScriptElement)) {
      return fallbackValue;
    }
    try {
      const parsed = JSON.parse(script.textContent || '');
      return parsed ?? fallbackValue;
    } catch (_error) {
      return fallbackValue;
    }
  },

  _cacheDomRefs() {
    if (!(this.rootElement instanceof HTMLElement)) {
      return;
    }
    const rowsList = this.rootElement.querySelector('[x-ref="rowsList"]');
    if (rowsList instanceof HTMLElement) {
      this.rowsListElement = rowsList;
    }
    const pageSummary = this.rootElement.querySelector('[x-ref="pageSummary"]');
    if (pageSummary instanceof HTMLElement) {
      this.pageSummaryElement = pageSummary;
    }
    const completionLabel = this.rootElement.querySelector(
      '[x-ref="completionLabel"]',
    );
    if (completionLabel instanceof HTMLElement) {
      this.completionLabelElement = completionLabel;
    }
    const summaryBreakdown = this.rootElement.querySelector(
      '[x-ref="summaryBreakdown"]',
    );
    if (summaryBreakdown instanceof HTMLElement) {
      this.summaryBreakdownElement = summaryBreakdown;
    }
    const progressBar = this.rootElement.querySelector('[x-ref="progressBar"]');
    if (progressBar instanceof HTMLElement) {
      this.progressBarElement = progressBar;
    }
    const prioritySummary = this.rootElement.querySelector(
      '[x-ref="prioritySummary"]',
    );
    if (prioritySummary instanceof HTMLElement) {
      this.prioritySummaryElement = prioritySummary;
    }
    // statusTabs rendering is now reactive via Alpine x-for in underline_tabs.html
    const showingSummary = this.rootElement.querySelector(
      '[x-ref="showingSummary"]',
    );
    if (showingSummary instanceof HTMLElement) {
      this.showingSummaryElement = showingSummary;
    }
    const emptyTitle = this.rootElement.querySelector('[x-ref="emptyTitle"]');
    if (emptyTitle instanceof HTMLElement) {
      this.emptyTitleElement = emptyTitle;
    }
    const emptyDescription = this.rootElement.querySelector(
      '[x-ref="emptyDescription"]',
    );
    if (emptyDescription instanceof HTMLElement) {
      this.emptyDescriptionElement = emptyDescription;
    }
  },

  _loadInitialState() {
    this.summary = this._parseJsonScript(
      'action-items-summary-data',
      this.summary,
    );
    this.statusTabs = this._parseJsonScript('action-items-tabs-data', []);
    const filters = this._parseJsonScript('action-items-filters-data', {});
    this.priorityOptions = Array.isArray(filters.priority_options)
      ? singleSelectDropdown.normalizeOptions(
          filters.priority_options,
          this.priorityOptions,
        )
      : this.priorityOptions;
    this.topicOptions = this._normalizeTopicOptions(
      filters.topic_options || [],
    );
    this.status = String(this.rootElement?.dataset?.initialStatus || 'all');
    this.priority = String(this.rootElement?.dataset?.initialPriority || 'all');
    this.searchQuery = String(this.rootElement?.dataset?.initialSearch || '');
    this.selectedTopics = this._normalizeSelectedTopics(filters);
    this.pagination = {
      ...this.pagination,
      page:
        Number.parseInt(this.rootElement?.dataset?.initialPage || '1', 10) || 1,
      total_pages:
        Number.parseInt(
          this.rootElement?.dataset?.initialTotalPages || '1',
          10,
        ) || 1,
      has_next: this.rootElement?.dataset?.initialHasNext === '1',
      has_previous: this.rootElement?.dataset?.initialHasPrevious === '1',
      total_matching:
        Number.parseInt(
          this.rootElement?.dataset?.initialTotalMatching || '0',
          10,
        ) || 0,
    };
    this.hasResults =
      this.rowsListElement instanceof HTMLElement &&
      this.rowsListElement.children.length > 0;
    this.emptyState = {
      ...this.emptyState,
      title: String(filters?.empty_state?.title || this.emptyState.title),
      description: String(
        filters?.empty_state?.description || this.emptyState.description,
      ),
    };

    // Load action item detail data for evidence drawer
    this.actionItemDetailData = this._parseJsonScript(
      'action-items-detail-data',
      {},
    );
  },

  _normalizeTopicOptions(options) {
    return searchableMultiSelect.normalizeObjectOptions(options);
  },

  _normalizeSelectedTopics(filters) {
    const rawTopics = Array.isArray(filters?.topics)
      ? filters.topics
      : filters?.topic
        ? [filters.topic]
        : [];
    const normalized = [];
    const seen = new Set();
    rawTopics.forEach((topic) => {
      const key = searchableMultiSelect.selectionKey(topic);
      if (!key || key === 'all' || seen.has(key)) {
        return;
      }
      seen.add(key);
      normalized.push(key);
    });
    return normalized;
  },

  _loadBaseParams() {
    this.baseParams = new URLSearchParams();
    if (!(this.rootElement instanceof HTMLElement)) {
      return;
    }
    const params = this.rootElement.querySelectorAll(
      '[data-action-items-base-param]',
    );
    params.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) {
        return;
      }
      if (!input.name || input.value === '') {
        return;
      }
      this.baseParams.append(String(input.name), String(input.value));
    });
    this._stripManagedActionParams(this.baseParams);
  },

  _stripManagedActionParams(params) {
    if (!(params instanceof URLSearchParams)) {
      return;
    }
    [
      'action_status',
      'action_priority',
      'action_topic',
      'action_search',
      'action_page',
    ].forEach((key) => params.delete(key));
  },

  priorityButtonLabel() {
    return singleSelectDropdown.label(
      this.priorityOptions,
      this.priority,
      'Priority',
    );
  },

  topicButtonLabel() {
    return searchableMultiSelect.buttonLabel(
      this.selectedTopics,
      this.topicOptions,
      { emptyLabel: 'All topics' },
    );
  },

  togglePriorityDropdown() {
    const nextState = !this.priorityDropdownOpen;
    if (nextState) {
      this.closeTopicDropdown();
    }
    this.priorityDropdownOpen = nextState;
  },

  closePriorityDropdown() {
    this.priorityDropdownOpen = false;
  },

  toggleTopicDropdown() {
    if (!this.topicDropdownOpen) {
      this.closePriorityDropdown();
    }
    this.topicDropdownOpen = !this.topicDropdownOpen;
    if (this.topicDropdownOpen) {
      this.activeTopicIndex = 0;
      this.$nextTick(() => {
        const topicSearchInput = this.$refs.topicSearchInput;
        if (topicSearchInput instanceof HTMLInputElement) {
          topicSearchInput.focus();
        }
      });
    } else {
      this.topicSearch = '';
    }
  },

  closeTopicDropdown() {
    this.topicDropdownOpen = false;
    this.topicSearch = '';
    this.activeTopicIndex = 0;
  },

  setStatus(nextStatus) {
    const normalized = String(nextStatus || 'all')
      .trim()
      .toLowerCase();
    const nextValue = ['all', 'open', 'in_progress', 'completed'].includes(
      normalized,
    )
      ? normalized
      : 'all';
    if (this.status === nextValue) {
      return;
    }
    this.status = nextValue;
    this.applyFilters();
  },

  setPriority(nextPriority) {
    const nextValue = singleSelectDropdown.resolveValue(
      nextPriority,
      this.priorityOptions,
      'all',
    );
    if (this.priority === nextValue) {
      this.closePriorityDropdown();
      return;
    }
    this.priority = nextValue;
    this.closePriorityDropdown();
    this.applyFilters();
  },

  handleSelectChange(detail) {
    if (detail.name === 'priority') {
      this.setPriority(detail.value);
    }
    if (detail.name === 'topics') {
      this.selectedTopics = Array.isArray(detail.value) ? detail.value : [];
      this.applyFilters();
    }
  },

  _findTopicOption(topicKey) {
    return searchableMultiSelect.findOption(this.topicOptions, topicKey);
  },

  _matchesQuery(query, searchableText) {
    return filterSearchUtils.matchesLabelQuery(query, searchableText);
  },

  filteredTopicOptions() {
    return searchableMultiSelect.filterOptions(
      this.topicOptions,
      this.topicSearch,
    );
  },

  isTopicSelected(topicKey) {
    return searchableMultiSelect.isSelected(this.selectedTopics, topicKey);
  },

  toggleTopic(topicKey) {
    const key = searchableMultiSelect.selectionKey(topicKey);
    if (!key) {
      return;
    }
    if (this.isTopicSelected(key)) {
      this.selectedTopics = this.selectedTopics.filter(
        (selectedTopic) => selectedTopic !== key,
      );
    } else {
      this.selectedTopics = [...this.selectedTopics, key];
    }
    this.topicSearch = '';
    this.activeTopicIndex = 0;
    this.applyFilters();
  },

  addTopicFilter(topicKey) {
    const key = searchableMultiSelect.selectionKey(topicKey);
    if (!key || this.isTopicSelected(key)) return;
    this.$dispatch('select-command', {
      name: 'topics',
      action: 'set',
      value: [...this.selectedTopics, key],
    });
  },

  toggleTopicChipFilter(topicKey) {
    this.$dispatch('select-command', {
      name: 'topics',
      action: 'remove',
      key: searchableMultiSelect.selectionKey(topicKey),
    });
  },

  selectedTopicChips() {
    return searchableMultiSelect.selectedChips(
      this.selectedTopics,
      this.topicOptions,
    );
  },

  removeTopic(topicKey) {
    this.$dispatch('select-command', {
      name: 'topics',
      action: 'remove',
      key: searchableMultiSelect.selectionKey(topicKey),
    });
  },

  focusNextTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      this.activeTopicIndex = 0;
      return;
    }
    this.activeTopicIndex = Math.min(
      this.activeTopicIndex + 1,
      options.length - 1,
    );
  },

  focusPreviousTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      this.activeTopicIndex = 0;
      return;
    }
    this.activeTopicIndex = Math.max(this.activeTopicIndex - 1, 0);
  },

  selectActiveTopicOption() {
    const options = this.filteredTopicOptions();
    if (!options.length) {
      return;
    }
    const selected = options[this.activeTopicIndex];
    if (selected) {
      this.toggleTopic(selected.value);
    }
  },

  onSearchInput() {
    this.searchRevision += 1;
    if (this.searchDebounceTimer) {
      window.clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = window.setTimeout(() => {
      this.applyFilters();
    }, 280);
  },

  hasActiveFilters() {
    return Boolean(
      this.status !== 'all' ||
        this.priority !== 'all' ||
        this.selectedTopics.length ||
        this.searchQuery.trim(),
    );
  },

  clearAll() {
    this.status = 'all';
    this.searchQuery = '';
    this.topicSearch = '';
    this.closePriorityDropdown();
    this.closeTopicDropdown();
    this.$dispatch('select-command', { name: 'topics', action: 'clear' });
    this.$dispatch('select-command', {
      name: 'priority',
      action: 'set',
      value: 'all',
    });
    this.applyFilters();
  },

  currentViewUrl(page = null) {
    const params = this._buildParams(page || this.pagination.page || 1);
    const query = params.toString();
    return query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
  },

  pageLabel() {
    const currentPage = Number(this.pagination.page || 1);
    const totalPages = Math.max(Number(this.pagination.total_pages || 1), 1);
    return `${currentPage} of ${totalPages}`;
  },

  goToPage(nextPage) {
    const pageNumber = Number(nextPage || 1);
    if (
      this.isLoading ||
      pageNumber < 1 ||
      pageNumber > Math.max(Number(this.pagination.total_pages || 1), 1)
    ) {
      return;
    }
    this._fetchPage(pageNumber);
  },

  applyFilters() {
    if (this._applyPending) return;
    this._applyPending = true;
    var self = this;
    queueMicrotask(function () {
      self._applyPending = false;
      self.closePriorityDropdown();
      self.closeTopicDropdown();
      self._fetchPage(1);
    });
  },

  _buildParams(page = 1) {
    const params = new URLSearchParams(this.baseParams.toString());
    this._stripManagedActionParams(params);
    if (this.status !== 'all') {
      params.set('action_status', this.status);
    }
    if (this.priority !== 'all') {
      params.set('action_priority', this.priority);
    }
    if (this.selectedTopics.length) {
      params.set('action_topic', this.selectedTopics.join(','));
    }
    const search = String(this.searchQuery || '').trim();
    if (search) {
      params.set('action_search', search);
    }
    if (page > 1) {
      params.set('action_page', String(page));
    }
    return params;
  },

  _renderRowsHtml(html, { append = false } = {}) {
    this._cacheDomRefs();
    asyncPaginatedList.renderListHtml(this.rowsListElement, html, {
      append,
      initializeTree: true,
    });
  },

  _updateHeaderSummary() {
    if (this.pageSummaryElement instanceof HTMLElement) {
      this.pageSummaryElement.textContent = `${Number(
        this.summary.total || 0,
      ).toLocaleString()} action items · ${Number(
        this.summary.open || 0,
      ).toLocaleString()} open · ${Number(
        this.summary.completed || 0,
      ).toLocaleString()} completed`;
    }
    if (this.completionLabelElement instanceof HTMLElement) {
      this.completionLabelElement.textContent = String(
        this.summary.completion_label || '0/0',
      );
    }
    if (this.progressBarElement instanceof HTMLElement) {
      this.progressBarElement.style.width = `${Number(
        this.summary.progress_percent || 0,
      )}%`;
    }
    if (this.summaryBreakdownElement instanceof HTMLElement) {
      this.summaryBreakdownElement.textContent = `${Number(
        this.summary.open || 0,
      ).toLocaleString()} open · ${Number(
        this.summary.in_progress || 0,
      ).toLocaleString()} in progress · ${Number(
        this.summary.completed || 0,
      ).toLocaleString()} completed`;
    }
  },

  _renderPrioritySummary() {
    if (!(this.prioritySummaryElement instanceof HTMLElement)) {
      return;
    }
    const cards = Array.isArray(this.summary.priority_summary)
      ? this.summary.priority_summary
      : [];
    this.prioritySummaryElement.innerHTML = cards
      .map(
        (priority) => `
          <div class="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 text-center dark:border-slate-800 dark:bg-slate-950/60">
            <p class="text-xl font-semibold ${String(
              priority.text_class || '',
            )}">${Number(priority.count || 0).toLocaleString()}</p>
            <p class="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">${String(
              priority.label || '',
            ).replace(' Priority', '')}</p>
          </div>
        `,
      )
      .join('');
  },

  _renderStatusTabs() {
    // The underline_tabs.html component renders reactively from this.statusTabs.
    // Just ensure show_count is set on each tab for the badge display.
    this.statusTabs = this.statusTabs.map((tab) => ({
      ...tab,
      show_count: tab.show_count !== false,
    }));
  },

  _updateShowingSummary(showingSummary = {}) {
    if (!(this.showingSummaryElement instanceof HTMLElement)) {
      return;
    }
    const filteredCount = Number(showingSummary.filtered_count || 0);
    const totalInRange = Number(showingSummary.total_in_range || 0);
    this.showingSummaryElement.textContent = `Showing ${filteredCount.toLocaleString()} of ${totalInRange.toLocaleString()} action items`;
  },

  _updateEmptyState() {
    if (this.emptyTitleElement instanceof HTMLElement) {
      this.emptyTitleElement.textContent = String(
        this.emptyState.title || 'No action items yet',
      );
    }
    if (this.emptyDescriptionElement instanceof HTMLElement) {
      this.emptyDescriptionElement.textContent = String(
        this.emptyState.description || '',
      );
    }
  },

  _syncUrlFromFilters(page = null) {
    const nextUrl = this.currentViewUrl(page);
    window.history.replaceState({}, '', nextUrl);
  },

  _syncFiltersFromPayload(filters, requestContext = null) {
    if (!filters || typeof filters !== 'object') {
      return;
    }
    this.status = String(filters.status || this.status || 'all');
    this.priority = String(filters.priority || this.priority || 'all');
    if (typeof filters.search_query === 'string') {
      const canSyncSearch =
        !requestContext ||
        requestContext.searchRevision === this.searchRevision;
      if (canSyncSearch) {
        this.searchQuery = filters.search_query;
      }
    }
    if (Array.isArray(filters.priority_options)) {
      this.priorityOptions = singleSelectDropdown.normalizeOptions(
        filters.priority_options,
        this.priorityOptions,
      );
    }
    if (Array.isArray(filters.topic_options)) {
      this.topicOptions = this._normalizeTopicOptions(filters.topic_options);
    }
    this.selectedTopics = this._normalizeSelectedTopics(filters);
    this.$nextTick(() => {
      this.$dispatch('options-update', {
        name: 'topics',
        options: this.topicOptions.map((o) => ({
          key: o.value || o.key,
          label: o.label,
        })),
        value: this.selectedTopics || [],
      });
    });
  },

  _applyPayload(payload, { page = 1, requestContext = null } = {}) {
    this.summary = payload?.summary || this.summary;
    this.statusTabs = Array.isArray(payload?.tabs)
      ? payload.tabs
      : this.statusTabs;
    this.pagination = {
      ...this.pagination,
      ...(payload?.pagination || {}),
    };
    this.emptyState = payload?.empty_state || this.emptyState;
    this._syncFiltersFromPayload(payload?.filters || {}, requestContext);
    this._renderRowsHtml(payload?.rows_html || '');
    this.hasResults = !Boolean(payload?.empty);
    this._updateHeaderSummary();
    this._renderPrioritySummary();
    this._renderStatusTabs();
    this._updateShowingSummary(payload?.showing_summary || {});
    this._updateEmptyState();
    this._syncUrlFromFilters(this.pagination.page || page);
  },

  async _fetchPage(page) {
    return asyncPaginatedList.fetchPage(this, {
      page,
      buildParams: (nextPage) => this._buildParams(nextPage),
      buildRequestContext: () => ({
        searchRevision: this.searchRevision,
      }),
      applyPayload: (payload, { page: requestedPage, requestContext }) => {
        this.hasError = false;
        this.errorMessage = '';
        this._applyPayload(payload, { page: requestedPage, requestContext });
      },
      onError: (error) => {
        this.hasError = true;
        this.errorMessage =
          (error && error.message) || 'Couldn’t load action items. Try again.';
        showActionItemToast({
          message: 'Unable to update action items right now.',
          tone: 'error',
        });
      },
    });
  },

  retry() {
    this.hasError = false;
    this.errorMessage = '';
    this._fetchPage(this.pagination?.page || 1);
  },

  openEvidenceDrawer(activityId) {
    const item = this.actionItemDetailData[String(activityId)];
    if (!item) return;
    this.activeActionItem = item;
    this.evidenceDrawerOpen = true;
    this.evidenceHtml = '';
    this.evidenceError = '';
    if (item.supporting_evidence_url) {
      this.evidenceLoading = true;
      fetch(item.supporting_evidence_url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      })
        .then((r) => {
          if (!r.ok) throw new Error('Failed to load evidence');
          return r.json();
        })
        .then((data) => {
          this.evidenceHtml = String(data.cards_html || '');
          this.evidenceLoading = false;
          this._renderEvidenceHtml();
        })
        .catch(() => {
          this.evidenceError = 'Failed to load evidence.';
          this.evidenceLoading = false;
        });
    }
  },

  _renderEvidenceHtml() {
    const container = this.$refs?.evidenceCardsContainer;
    if (!(container instanceof HTMLElement)) return;
    container.innerHTML = this.evidenceHtml;
  },

  closeEvidenceDrawer() {
    this.evidenceDrawerOpen = false;
    setTimeout(() => {
      this.activeActionItem = null;
      this.evidenceHtml = '';
      this.evidenceError = '';
      const container = this.$refs?.evidenceCardsContainer;
      if (container instanceof HTMLElement) container.innerHTML = '';
    }, 500);
  },

  async submitStatusUpdate(event) {
    const form = event?.target;
    if (!(form instanceof HTMLFormElement) || this.isLoading) {
      return;
    }
    const submitter =
      event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : form.querySelector('button[type="submit"]');
    if (!(submitter instanceof HTMLButtonElement)) {
      return;
    }

    const originalLabel = submitter.textContent || '';
    submitter.disabled = true;
    submitter.textContent = String(
      submitter.dataset.pendingLabel || 'Updating...',
    );

    const formData = new FormData(form);
    formData.set('next', this.currentViewUrl());
    const body = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') {
        body.append(key, value);
      }
    }
    const csrfToken =
      String(formData.get('csrfmiddlewaretoken') || '') ||
      getFormCsrfToken(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        credentials: 'same-origin',
        body,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
      });
      const payload = await response
        .json()
        .catch(() => ({ error: 'Unable to update action item right now.' }));
      if (!response.ok) {
        throw new Error(
          String(payload.error || 'Unable to update action item right now.'),
        );
      }
      this._applyPayload(payload, { page: this.pagination.page || 1 });
      showActionItemToast({
        message: String(payload.toast_message || 'Action item updated.'),
        tone: 'success',
      });
    } catch (error) {
      submitter.disabled = false;
      submitter.textContent = originalLabel;
      showActionItemToast({
        message:
          error instanceof Error
            ? String(error.message || 'Unable to update action item right now.')
            : 'Unable to update action item right now.',
        tone: 'error',
      });
    }
  },
}));

Alpine.data('topicsTableFilter', (initialQuery = '') => ({
  searchQuery: initialQuery || '',

  init() {},

  filterRows() {
    const query = String(this.searchQuery || '')
      .trim()
      .toLowerCase();
    const table = this.$refs.topicsTable;
    if (!table) return;
    const rows = table.querySelectorAll('tbody tr[data-topic-label]');
    const emptyState = table.querySelector('tr[data-empty-state]');
    let visibleCount = 0;

    rows.forEach((row) => {
      const label = (row.getAttribute('data-topic-label') || '').toLowerCase();
      const match = !query || label.includes(query);
      row.style.display = match ? '' : 'none';
      if (match) visibleCount++;
    });

    // Toggle server-side empty state vs client-side no-results
    if (emptyState) {
      emptyState.style.display = query ? 'none' : '';
    }
    const noResults = this.$refs.noSearchResults;
    if (noResults) {
      noResults.classList.toggle('hidden', visibleCount > 0 || !query);
    }
  },

  hasActiveSearch() {
    return Boolean(String(this.searchQuery || '').trim());
  },
}));

Alpine.data('dropdown', () => ({
  open: false,
  // CSS string written into the panel's :style binding. The panel uses
  // `position: fixed` so it escapes any `overflow: hidden` ancestor card
  // (which `absolute` cannot). _computePlacement() recomputes top/right on
  // every open and decides whether to flip above the trigger when there isn't
  // enough room below.
  panelStyle: '',
  // Approximate panel height in px used for the flip heuristic. Override per
  // call site with `data-dropdown-expected-height="160"` on the x-data root.
  expectedHeight: 200,
  // Gap between trigger and panel in px.
  panelGap: 8,
  init() {
    var attr = this.$el.dataset.dropdownExpectedHeight;
    if (attr) {
      var parsed = parseInt(attr, 10);
      if (parsed > 0) this.expectedHeight = parsed;
    }
    // Recompute on scroll/resize while open so the fixed panel tracks the
    // trigger. Use capture phase to catch nested scroll containers.
    var self = this;
    this._reflowHandler = function () {
      if (self.open) self._computePlacement();
    };
    window.addEventListener('scroll', this._reflowHandler, true);
    window.addEventListener('resize', this._reflowHandler);
  },
  destroy() {
    if (this._reflowHandler) {
      window.removeEventListener('scroll', this._reflowHandler, true);
      window.removeEventListener('resize', this._reflowHandler);
    }
  },
  toggle() {
    if (!this.open) this._computePlacement();
    this.open = !this.open;
  },
  close() {
    this.open = false;
  },
  // Close the dropdown and dispatch a `show-modal` window event built from
  // `data-modal-*` attributes on the click target. Lets templates pass URLs
  // and free-form strings via plain HTML attributes (auto-escaped by the
  // templating engine) instead of inline JS literals — which avoids both
  // Alpine CSP's no-multi-statement rule and Django escapejs unicode pitfalls.
  dispatchModalFromTarget(modalId, target) {
    if (!(target instanceof HTMLElement)) return;
    this.open = false;
    window.dispatchEvent(
      new CustomEvent('show-modal', {
        detail: {
          id: modalId,
          action: target.dataset.modalAction || '',
          title: target.dataset.modalTitle || '',
          body: target.dataset.modalBody || '',
        },
      }),
    );
  },
  _computePlacement() {
    var triggerRect = this.$el.getBoundingClientRect();
    var spaceBelow = window.innerHeight - triggerRect.bottom;
    var spaceAbove = triggerRect.top;
    var flipUp = spaceBelow < this.expectedHeight && spaceAbove > spaceBelow;
    // Pin the panel's right edge to the trigger's right edge so the menu
    // expands leftward (matches the original `right-0 origin-top-right` look).
    var right = Math.max(window.innerWidth - triggerRect.right, 0);
    if (flipUp) {
      var bottom = window.innerHeight - triggerRect.top + this.panelGap;
      this.panelStyle =
        'position: fixed; right: ' +
        right +
        'px; bottom: ' +
        bottom +
        'px; transform-origin: bottom right;';
    } else {
      var top = triggerRect.bottom + this.panelGap;
      this.panelStyle =
        'position: fixed; right: ' +
        right +
        'px; top: ' +
        top +
        'px; transform-origin: top right;';
    }
  },
}));

Alpine.data('selectMenu', () => ({
  open: false,
  value: '', // string for single, array for multi
  options: [],
  placeholder: 'Select...',
  name: '',
  multi: false,
  searchable: false,
  search: '',
  focusedIndex: -1,
  _outsideHandler: null,

  init() {
    var configEl = this.$el.querySelector('script[data-select-config]');
    if (configEl) {
      try {
        var cfg = JSON.parse(configEl.textContent);
        this.options = cfg.options || [];
        this.placeholder = cfg.placeholder || this.placeholder;
        this.name = cfg.name || '';
        this.multi = !!cfg.multi;
        this.searchable = !!cfg.searchable;
        if (this.multi) {
          this.value = Array.isArray(cfg.value)
            ? cfg.value
            : cfg.value
              ? [cfg.value]
              : [];
        } else {
          this.value = cfg.value || '';
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Wire click handlers programmatically (Alpine CSP doesn't bind @click
    // on child elements inside {% include %} templates reliably).
    var self = this;
    var btn = this.$el.querySelector('[data-select-toggle]');
    if (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggle();
      });
    }

    // Delegate option clicks via the listbox container
    var listbox = this.$el.querySelector('[role="listbox"]');
    if (listbox) {
      listbox.addEventListener('click', function (e) {
        var option = e.target.closest('[role="option"]');
        if (option) {
          var key = option.dataset.key;
          if (key !== undefined) {
            self.select(key);
          }
        }
      });
    }
  },

  toggle() {
    if (this.open) {
      this._removeOutsideListener();
      this.open = false;
      this.focusedIndex = -1;
      this.search = '';
    } else {
      this.open = true;
      this.focusedIndex = 0;
      if (!this.multi && this.value) {
        var idx = this.visibleOptions().findIndex(
          (o) => String(o.key) === String(this.value),
        );
        if (idx >= 0) this.focusedIndex = idx;
      }
      this._addOutsideListener();
      if (this.searchable) {
        var self = this;
        this.$nextTick(function () {
          var input = self.$el.querySelector('input[type="search"]');
          if (input) input.focus();
        });
      }
    }
  },

  close() {
    this._removeOutsideListener();
    this.open = false;
    this.focusedIndex = -1;
    this.search = '';
  },

  _addOutsideListener() {
    this._removeOutsideListener();
    var el = this.$el;
    var self = this;
    requestAnimationFrame(function () {
      self._outsideHandler = function (e) {
        if (!el.contains(e.target)) {
          self.close();
        }
      };
      document.addEventListener('pointerdown', self._outsideHandler, true);
    });
  },

  _removeOutsideListener() {
    if (this._outsideHandler) {
      document.removeEventListener('pointerdown', this._outsideHandler, true);
      this._outsideHandler = null;
    }
  },

  destroy() {
    this._removeOutsideListener();
  },

  select(key) {
    if (this.multi) {
      var arr = Array.isArray(this.value) ? this.value.slice() : [];
      var idx = arr.indexOf(key);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(key);
      }
      this.value = arr;
      this.$dispatch('select-change', { name: this.name, value: arr });
    } else {
      this.value = key;
      this.open = false;
      this.focusedIndex = -1;
      this.search = '';
      this._removeOutsideListener();
      this.$dispatch('select-change', { name: this.name, value: key });
    }
  },

  selectedLabel() {
    if (this.multi) {
      var arr = Array.isArray(this.value) ? this.value : [];
      if (arr.length === 0) return this.placeholder;
      if (arr.length === 1) {
        var found = this.options.find((o) => String(o.key) === String(arr[0]));
        return found ? found.label : arr[0];
      }
      return arr.length + ' selected';
    }
    if (!this.value) return this.placeholder;
    var found = this.options.find((o) => String(o.key) === String(this.value));
    return found ? found.label : this.placeholder;
  },

  isSelected(key) {
    if (this.multi) {
      return Array.isArray(this.value) && this.value.indexOf(key) >= 0;
    }
    return String(this.value) === String(key);
  },

  visibleOptions() {
    if (!this.searchable || !this.search) return this.options;
    var q = this.search.toLowerCase();
    return this.options.filter(function (o) {
      return o.label.toLowerCase().indexOf(q) >= 0;
    });
  },

  onKeydown(event) {
    var visible = this.visibleOptions();
    if (!this.open) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        this.toggle();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusedIndex = Math.min(this.focusedIndex + 1, visible.length - 1);
      this.scrollFocusedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
      this.scrollFocusedIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.focusedIndex >= 0 && this.focusedIndex < visible.length) {
        this.select(visible[this.focusedIndex].key);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  },

  scrollFocusedIntoView() {
    var list = this.$el.querySelector('[role="listbox"]');
    if (!list) return;
    var items = list.querySelectorAll('[role="option"]');
    if (items[this.focusedIndex]) {
      items[this.focusedIndex].scrollIntoView({ block: 'nearest' });
    }
  },

  isFocused(idx) {
    return this.focusedIndex === idx;
  },

  onOptionsUpdate(detail) {
    if (detail && detail.name === this.name) {
      this.options = detail.options || [];
      if (this.multi) {
        if (Array.isArray(detail.value)) {
          this.value = detail.value;
        }
      } else {
        if (typeof detail.value === 'string') {
          this.value = detail.value;
        } else if (
          this.value &&
          !this.options.some((o) => String(o.key) === String(this.value))
        ) {
          this.value = '';
          this.$dispatch('select-change', { name: this.name, value: '' });
        }
      }
    }
  },

  // Command handler: external code dispatches 'select-command' to mutate
  // the selectMenu value without bypassing it. This keeps selectMenu as the
  // single source of truth.
  //   {name, action: 'remove', key}  – remove one key (multi)
  //   {name, action: 'clear'}        – reset to default
  //   {name, action: 'set', value}   – replace entire value
  onCommand(detail) {
    if (!detail || detail.name !== this.name) return;
    if (detail.action === 'remove' && this.multi) {
      var arr = Array.isArray(this.value) ? this.value.slice() : [];
      var idx = arr.indexOf(detail.key);
      if (idx >= 0) {
        arr.splice(idx, 1);
        this.value = arr;
      }
    } else if (detail.action === 'clear') {
      this.value = this.multi ? [] : '';
    } else if (detail.action === 'set') {
      this.value = detail.value;
    }
    // Always dispatch so the parent stays in sync
    this.$dispatch('select-change', { name: this.name, value: this.value });
  },
}));

let _csrfTokenCache = null;
function getCsrfToken() {
  if (_csrfTokenCache) return _csrfTokenCache;
  const input = document.querySelector('input[name="csrfmiddlewaretoken"]');
  _csrfTokenCache =
    (input && input.value) ||
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrftoken='))
      ?.split('=')[1] ||
    '';
  return _csrfTokenCache;
}

Alpine.data('roleSelect', function () {
  return {
    saving: false,
    url: '',
    original: '',

    init() {
      this.url = this.$el.dataset.url || '';
      this.original = this.$el.dataset.original || '';

      this.$el.addEventListener('select-change', (e) => {
        this.changeRole(e.detail.value);
      });
    },

    _revert(message) {
      this.$dispatch('select-command', {
        name: 'role-' + this.$el.dataset.memberId,
        action: 'set',
        value: this.original,
      });
      enqueueToast({ message, tone: 'error' });
    },

    async changeRole(newRole) {
      if (this.saving || newRole === this.original) return;

      this.saving = true;
      const body = new FormData();
      body.append('role', newRole);

      try {
        const response = await fetch(this.url, {
          method: 'POST',
          credentials: 'same-origin',
          body,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': getCsrfToken(),
          },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok && payload.success) {
          this.original = newRole;
          enqueueToast({ message: 'Role updated.', tone: 'success' });
        } else {
          this._revert(payload.error || 'Unable to update role.');
        }
      } catch {
        this._revert('Unable to update role.');
      } finally {
        this.saving = false;
      }
    },
  };
});

Alpine.data('collapsibleSidebar', collapsibleSidebar);
Alpine.data('bottomTabBar', bottomTabBar);

// Pricing page: monthly/annual toggle. Reads/writes localStorage under
// `datatru.pricing.cadence` and broadcasts `pricing-cadence-change` so the
// page-level x-data wrapper and any other listeners can react.
Alpine.data('pricingToggle', () => ({
  cadence: 'monthly',
  init() {
    const fallback = this.$el.dataset.defaultCadence || 'monthly';
    this.cadence = localStorage.getItem('datatru.pricing.cadence') || fallback;
    window.dispatchEvent(
      new CustomEvent('pricing-cadence-change', { detail: this.cadence }),
    );
  },
  set(next) {
    if (this.cadence === next) return;
    this.cadence = next;
    localStorage.setItem('datatru.pricing.cadence', next);
    if (window.DataTru && window.DataTru.track) {
      window.DataTru.track('annual_toggle', { state: next });
    }
    window.dispatchEvent(
      new CustomEvent('pricing-cadence-change', { detail: next }),
    );
  },
  setMonthly() {
    this.set('monthly');
  },
  setAnnual() {
    this.set('annual');
  },
  isMonthly() {
    return this.cadence === 'monthly';
  },
  isAnnual() {
    return this.cadence === 'annual';
  },
}));

// Pricing tier grid: at mobile widths the grid switches to a horizontal
// scroll-snap carousel (.pricing-tier-grid). When the volume slider's
// recommendation changes, scroll the matching card into view so the user
// doesn't have to hunt for it.
Alpine.data('pricingTierCarousel', () => ({
  _mql: null,
  init() {
    this._mql = window.matchMedia('(max-width: 639.98px)');
    window.addEventListener('pricing-recommendation-change', (e) => {
      if (!this._mql.matches) return;
      const familyKey = e.detail;
      if (!familyKey) return;
      const card = this.$el.querySelector(
        `[data-testid="plan-card-${familyKey}"]`,
      );
      if (!card) return;
      // Avoid scroll when the card is already visible to keep manual
      // scrolling sticky.
      const cardRect = card.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();
      const fullyVisible =
        cardRect.left >= containerRect.left - 1 &&
        cardRect.right <= containerRect.right + 1;
      if (fullyVisible) return;
      card.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      });
    });
  },
}));

// Pricing page: volume slider with recommendation. Bands are loaded from a
// sibling `<script id="volume-bands" type="application/json">` blob.
Alpine.data('volumeSlider', () => ({
  bands: [],
  value: 5000,
  _timer: null,
  init() {
    const bandsEl = document.getElementById('volume-bands');
    if (bandsEl) {
      try {
        this.bands = JSON.parse(bandsEl.textContent);
      } catch {
        this.bands = [];
      }
    }
    this._broadcast();
  },
  recommended() {
    for (let i = 0; i < this.bands.length; i++) {
      if (this.value <= this.bands[i].max_reviews) {
        return this.bands[i].family_key;
      }
    }
    return this.bands.length
      ? this.bands[this.bands.length - 1].family_key
      : '';
  },
  recommendedLabel() {
    const rec = this.recommended();
    return rec ? rec.charAt(0).toUpperCase() + rec.slice(1) : '';
  },
  valueLabel() {
    return this.value.toLocaleString() + ' / mo';
  },
  _broadcast() {
    window.dispatchEvent(
      new CustomEvent('pricing-recommendation-change', {
        detail: this.recommended(),
      }),
    );
  },
  setBand(familyKey) {
    // Snap the slider to a representative value inside the band so the
    // recommendation lines up with the user's click. We pick a value
    // near the band ceiling, capped to the slider's max.
    const max = 100000;
    const targets = {
      starter: 2000,
      growth: 10000,
      scale: 50000,
      enterprise: max,
    };
    const next = targets[familyKey];
    if (typeof next === 'number') {
      this.value = Math.min(next, max);
      this._broadcast();
    }
  },
  onInput(event) {
    if (event && event.target) {
      this.value = Number(event.target.value);
    }
    this._broadcast();
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      if (window.DataTru && window.DataTru.track) {
        window.DataTru.track('volume_slider_used', {
          review_count: this.value,
          resulting_plan: this.recommended(),
        });
      }
    }, 400);
  },
}));

Alpine.data('inlineEdit', function () {
  return {
    editing: false,
    saving: false,
    value: '',
    draft: '',
    _csrfToken: '',
    _input: null,

    init() {
      const el = this.$el;
      this.value = el.dataset.value || '';
      this.draft = this.value;

      this._csrfToken = getCsrfToken();

      // CSP build can't use inline Alpine directives (@click, @keydown, x-model)
      this._handlers = [];
      const addListener = (el, evt, fn) => {
        el.addEventListener(evt, fn);
        this._handlers.push([el, evt, fn]);
      };

      const display = el.querySelector('[data-inline-edit-display]');
      if (display) {
        addListener(display, 'click', () => this.startEdit());
      }

      this._input = el.querySelector('[data-inline-edit-input]');
      if (this._input) {
        addListener(this._input, 'input', (e) => {
          this.draft = e.target.value;
          this._resizeInput();
        });
        addListener(this._input, 'keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this.cancel();
          }
        });
        addListener(this._input, 'blur', () => this.save());
      }

      this.$watch('editing', (val) => {
        if (val && this._input) {
          this.$nextTick(() => {
            this._input.value = this.draft;
            this._resizeInput();
            this._input.focus();
            this._input.select();
          });
        }
      });

      const displayText = el.querySelector('[data-inline-edit-text]');
      if (displayText) {
        this.$watch('value', (val) => {
          displayText.textContent = val;
        });
      }
    },

    destroy() {
      for (const [el, evt, fn] of this._handlers) {
        el.removeEventListener(evt, fn);
      }
      this._handlers = [];
      if (this._sizer) {
        this._sizer.remove();
        this._sizer = null;
      }
    },

    startEdit() {
      if (this.saving) return;
      this.draft = this.value;
      this.editing = true;
    },

    cancel() {
      this.draft = this.value;
      this.editing = false;
    },

    _resizeInput() {
      const input = this._input;
      if (!input) return;
      const container =
        input.closest('li, article, div.min-w-0') || input.parentElement;
      const maxWidth = container ? container.clientWidth : 300;
      // Measure content width via off-screen span (single reflow)
      if (!this._sizer) {
        this._sizer = document.createElement('span');
        this._sizer.style.cssText =
          'position:absolute;visibility:hidden;white-space:pre';
        document.body.appendChild(this._sizer);
      }
      const style = getComputedStyle(input);
      this._sizer.style.font = style.font;
      this._sizer.textContent = input.value || input.placeholder || '';
      const contentWidth = this._sizer.offsetWidth + 4;
      // Size to content with 80px minimum, capped at container width
      input.style.width = Math.min(Math.max(contentWidth, 80), maxWidth) + 'px';
    },

    async save() {
      if (this.saving) return;
      const trimmed = this.draft.trim();
      if (trimmed === this.value) {
        this.editing = false;
        return;
      }
      this.saving = true;
      const url = this.$el.dataset.url;
      const fieldName = this.$el.dataset.field || 'name';
      const body = new FormData();
      body.append(fieldName, trimmed);

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          body,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': this._csrfToken,
          },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok && payload.success) {
          this.value = payload[fieldName] || trimmed;
          this.editing = false;
        } else {
          const message = payload.error || 'Unable to save changes.';
          enqueueToast({ message, tone: 'error' });
        }
      } catch (err) {
        enqueueToast({ message: 'Unable to save changes.', tone: 'error' });
      } finally {
        this.saving = false;
      }
    },
  };
});

const isModifiedPointerClick = (event) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

const showActionItemToast = ({
  message,
  tone = 'success',
  linkLabel = '',
  linkHref = '',
}) => {
  enqueueToast({
    message: String(message || ''),
    tone,
    action:
      linkLabel && linkHref
        ? {
            label: String(linkLabel),
            href: String(linkHref),
          }
        : null,
  });
};

const getFormCsrfToken = (form) => {
  if (!(form instanceof HTMLFormElement)) {
    return '';
  }
  const tokenInput = form.querySelector('input[name="csrfmiddlewaretoken"]');
  if (!(tokenInput instanceof HTMLInputElement)) {
    return '';
  }
  return String(tokenInput.value || '');
};

const setActionItemButtonPending = (button) => {
  button.dataset.pending = '1';
  button.dataset.originalLabel =
    button.dataset.originalLabel || button.textContent;
  button.disabled = true;
  button.textContent = 'Creating...';
};

const setActionItemButtonCreated = (button) => {
  button.disabled = true;
  button.dataset.pending = '0';
  button.dataset.created = '1';
  button.textContent = button.dataset.createdLabel || '✓ Added';
  button.classList.remove(
    'bg-blue-600',
    'hover:bg-blue-700',
    'border-blue-300',
    'bg-blue-50',
    'text-blue-700',
    'hover:bg-blue-100',
    'dark:border-blue-500/60',
    'dark:bg-blue-500/10',
    'dark:text-blue-200',
    'dark:hover:bg-blue-500/20',
  );
  button.classList.add(
    'bg-emerald-600',
    'text-white',
    'hover:bg-emerald-600',
    'cursor-default',
  );
};

const setActionItemButtonIdle = (button) => {
  button.dataset.pending = '0';
  button.disabled = false;
  button.textContent = button.dataset.originalLabel || '+ Add to actions';
};

const upsertActionItemRow = ({ activityId, activityHtml }) => {
  const list = document.querySelector('[data-action-items-list]');
  if (!(list instanceof HTMLElement) || !activityId || !activityHtml) {
    return;
  }

  const emptyState = document.querySelector('[data-action-items-empty-state]');
  if (emptyState instanceof HTMLElement) {
    emptyState.classList.add('hidden');
  }
  list.classList.remove('hidden');

  const existing = list.querySelector(`[data-action-item-id="${activityId}"]`);
  if (existing instanceof HTMLElement) {
    return;
  }

  list.insertAdjacentHTML('afterbegin', activityHtml);
};

initializeToasts();

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-supporting-evidence-url]');
  if (!trigger || event.defaultPrevented || isModifiedPointerClick(event)) {
    return;
  }
  const drawerUrl = String(
    trigger.getAttribute('data-supporting-evidence-url') || '',
  ).trim();
  if (!drawerUrl) {
    return;
  }
  event.preventDefault();
  window.dispatchEvent(
    new CustomEvent('supporting-evidence:open', {
      detail: {
        drawerUrl,
        fallbackUrl: String(trigger.getAttribute('href') || '').trim(),
        triggerElement: trigger,
      },
    }),
  );
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  if (!form.matches('[data-action-item-form]')) {
    return;
  }
  event.preventDefault();

  const button =
    event.submitter instanceof HTMLButtonElement
      ? event.submitter
      : form.querySelector('[data-action-item-button]');
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }
  if (button.dataset.pending === '1' || button.dataset.created === '1') {
    return;
  }
  setActionItemButtonPending(button);

  const formData = new FormData(form);
  const body = new URLSearchParams();
  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      body.append(key, value);
    }
  }

  const csrfToken =
    String(formData.get('csrfmiddlewaretoken') || '') || getFormCsrfToken(form);

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
    });
    const payload = await response
      .json()
      .catch(() => ({ error: 'Unable to create task right now.' }));
    if (!response.ok) {
      throw new Error(
        String(payload.error || 'Unable to create task right now.'),
      );
    }
    setActionItemButtonCreated(button);

    const row = form.closest('[data-top-priority-row]');
    if (row instanceof HTMLElement) {
      const checkbox = row.querySelector('input[type="checkbox"]');
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = true;
        checkbox.disabled = true;
      }
    }

    upsertActionItemRow({
      activityId: String(payload.activity_id || ''),
      activityHtml: String(payload.activity_html || ''),
    });

    showActionItemToast({
      message: String(payload.toast_message || '✓ Added to Action Items'),
      tone: 'success',
    });
  } catch (error) {
    setActionItemButtonIdle(button);
    showActionItemToast({
      message:
        error instanceof Error
          ? String(error.message || 'Unable to create task right now.')
          : 'Unable to create task right now.',
      tone: 'error',
    });
  }
});

// Create the theme store and keep it aligned with the blocking script
const theme = themeToggle();
Alpine.store('theme', theme);

theme.initializeTheme();

window.Alpine = Alpine;
Alpine.start();

// Additional third-party libraries can be added below as needed.
// Example:
// import 'my-custom-library';

// IMPORTANT:
// Ensure that all libraries imported here are relevant for global use
// and are not tightly coupled with specific application components.
